import React, { useRef, useState, useEffect, useMemo, useCallback } from 'react';
import { useSaaSContext } from '../../store/SaaSContext';
import { useThreeDContext } from '../../store/ThreeDContext';
import { useToast } from '../../store/ToastContext';
import { 
  ZoomIn, ZoomOut, Hand, MousePointer, Box, Scissors, Eye, EyeOff, Palette, ShieldCheck, Target,
  ChevronLeft, ChevronRight, ChevronsLeft, ChevronsRight, Frame, QrCode, AlertTriangle,
  Info, Ruler, BarChart3, Ban, Maximize2, RotateCcw, SlidersHorizontal,
  Grid3x3, Space, Maximize, Zap, AlignCenter, RotateCw, Layers, CircleDot
} from 'lucide-react';
import { formatPrintSlug } from '../../utils/slugFormatter';
import { formatUnit, parseUnit } from '../../utils/units';
import { calculatePdfBoxes } from '../../utils/pdfBoxes';
import { TrimBoxMediaBoxPanel } from './TrimBoxMediaBoxPanel';
import { getActiveColorBarPatches } from '../../utils/colorBarsUtils';
import { ColorBarsConfigPanel } from './ColorBarsConfigPanel';
import { SafeZonePanel } from './SafeZonePanel';
import { PlotterFinishingPanel } from './PlotterFinishingPanel';
import { GangingAndNestingPanel } from './GangingAndNestingPanel';
import { DraggableFloatingWindow } from './DraggableFloatingWindow';
import { mapGridSlotsToGangingJobs, DEFAULT_GANGING_JOBS } from '../../utils/gangingBalancer';
import { Cuboid as CubeIcon } from 'lucide-react';
import { Ean13BarcodeSvg, UpcaBarcodeSvg, DataMatrixSvg, QrCodeSvg } from '../../utils/barcodeGenerators';
import { 
  getStarSvgPath, 
  getPolygonSvgPath, 
  getTriangleSvgPath, 
  getDiamondSvgPath, 
  getHeartSvgPath, 
  getArrowSvgPath, 
  getLineEffectSvgPath, 
  formatCurrencyValue, 
  formatDateValue 
} from '../../utils/shapeAndFormatUtils';
import { pointsToSvgPath } from '../../utils/vectorPathUtils';
import ReactBarcode from 'react-barcode';
import { ProofViewer3DModal } from './ProofViewer3DModal';
import { RoiCalculatorPanel } from './RoiCalculatorPanel';

const getStrokeDasharray = (lineDash?: string, strokeWidth: number = 1): string => {
  if (!lineDash || lineDash === 'solid' || lineDash === 'none') return 'none';
  if (lineDash === 'dashed') return `${strokeWidth * 3} ${strokeWidth * 2}`;
  if (lineDash === 'dotted') return `${strokeWidth} ${strokeWidth * 2}`;
  return lineDash;
};

const renderArrowheadDefs = (el: any) => {
  if (!el.arrowheadStart && !el.arrowheadEnd) return null;
  const color = el.borderColor || el.strokeColor || '#000000';
  
  return (
    <defs>
      {el.arrowheadStart && el.arrowheadStart !== 'none' && (
        <marker id={`imp-arrow-start-${el.id}`} markerWidth="10" markerHeight="10" refX="2" refY="5" orient="auto" markerUnits="strokeWidth">
          {el.arrowheadStart === 'arrow' && <path d="M 0 5 L 10 0 L 10 10 z" fill={color} />}
          {el.arrowheadStart === 'circle' && <circle cx="5" cy="5" r="3" fill={color} />}
          {el.arrowheadStart === 'square' && <rect x="2" y="2" width="6" height="6" fill={color} />}
        </marker>
      )}
      {el.arrowheadEnd && el.arrowheadEnd !== 'none' && (
        <marker id={`imp-arrow-end-${el.id}`} markerWidth="10" markerHeight="10" refX="8" refY="5" orient="auto" markerUnits="strokeWidth">
          {el.arrowheadEnd === 'arrow' && <path d="M 0 0 L 10 5 L 0 10 z" fill={color} />}
          {el.arrowheadEnd === 'circle' && <circle cx="5" cy="5" r="3" fill={color} />}
          {el.arrowheadEnd === 'square' && <rect x="2" y="2" width="6" height="6" fill={color} />}
        </marker>
      )}
    </defs>
  );
};

interface ImpositionSlotProps {
  r: number;
  c: number;
  rows: number;
  cols: number;
  stepX: number;
  stepY: number;
  bleedVal: number;
  stickerWidth: number;
  stickerHeight: number;
  previewScale: number;
  stickerVisualRotation: number;
  viewSide: string;
  impositionType: string;
  isGangingActive: boolean;
  gangingSlotMap: any;
  slotInteractionMode: string;
  disabledSlots: string[];
  toggleSlotDisabled: (r: number, c: number) => void;
  getPreviewRecordIndex: (r: number, c: number) => string;
  layout: any;
  template: any;
  pagesNeeded: number;
  previewPage: number;
  stickersPerPage: number;
  totalStickers: number;
  copies: number;
  csvData: any;
  effectiveLen: number;
  effectiveOffset: number;
}

const ImpositionSlot: React.FC<ImpositionSlotProps> = React.memo(({
  r, c, rows, cols, stepX, stepY, bleedVal, stickerWidth, stickerHeight, previewScale,
  stickerVisualRotation, viewSide, impositionType, isGangingActive, gangingSlotMap,
  slotInteractionMode, disabledSlots, toggleSlotDisabled, getPreviewRecordIndex,
  layout, template, pagesNeeded, previewPage, stickersPerPage, totalStickers, copies,
  csvData, effectiveLen, effectiveOffset
}) => {
  const slotKey = `${r}-${c}`;
  const isDisabled = disabledSlots.includes(slotKey);

  // Work & Turn Column / Row Mirrored Alignment calculation for Back Side
  const targetC = viewSide === 'back' && impositionType === 'work_and_turn' ? (cols - 1 - c) : c;
  const targetR = viewSide === 'back' && impositionType === 'work_and_tumble' ? (rows - 1 - r) : r;
  const targetSlotKey = `${targetR}-${targetC}`;

  const gangedJob = isGangingActive ? gangingSlotMap[targetSlotKey] : null;

  return (
    <div 
      onClick={() => {
        if (slotInteractionMode === 'skip_toggle') {
          toggleSlotDisabled(r, c);
        }
      }}
      className={`absolute flex flex-col items-center justify-center text-[10px] font-medium transition-all overflow-hidden ${
        isDisabled
          ? 'bg-red-950/80 border-2 border-red-500 text-red-200 z-30 shadow-md cursor-pointer'
          : slotInteractionMode === 'skip_toggle'
          ? 'bg-blue-100/60 border border-orange-400 hover:border-amber-500 hover:ring-2 hover:ring-amber-400 cursor-pointer text-blue-800'
          : gangedJob
          ? 'bg-white border-2 text-zinc-900 shadow-xs'
          : 'bg-white border border-orange-400 text-zinc-900'
      }`}
      style={{
        left: `${c * stepX + bleedVal}px`,
        top: `${r * stepY + bleedVal}px`,
        width: `${stickerWidth}px`,
        height: `${stickerHeight}px`,
        borderColor: gangedJob ? gangedJob.color : undefined,
        backgroundImage: isDisabled ? 'repeating-linear-gradient(45deg, rgba(239, 68, 68, 0.2), rgba(239, 68, 68, 0.2) 10px, transparent 10px, transparent 20px)' : undefined
      }}
      title={
        isDisabled 
          ? `Slot (${r + 1}, ${c + 1}) is SKIPPED/DISABLED. Click to re-enable.`
          : gangedJob
          ? `${gangedJob.clientName} - ${gangedJob.jobName} (Req: ${gangedJob.requiredQty})`
          : `Slot (${r + 1}, ${c + 1})`
      }
    >
      <div style={{ transform: stickerVisualRotation ? `rotate(${stickerVisualRotation}deg)` : 'none', width: '100%', height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center', overflow: 'hidden' }}>
      {isDisabled ? (
        <div className="flex flex-col items-center justify-center gap-1 p-1 text-center pointer-events-none">
          <span className="text-[11px] font-bold text-red-300 uppercase tracking-wider bg-red-900/90 px-1.5 py-0.5 rounded border border-red-500 shadow">
            🚫 SKIPPED
          </span>
          <span className="text-[9px] text-red-300 font-mono">Slot ({r + 1}, {c + 1})</span>
        </div>
      ) : gangedJob ? (
        /* Multi-Job Ganging Card Overlay */
        <div className="w-full h-full p-1 flex flex-col justify-between items-center text-center overflow-hidden pointer-events-none">
          <div 
            className="w-full py-0.5 text-[8px] font-bold text-white uppercase truncate px-1 rounded-xs shadow-xs" 
            style={{ backgroundColor: gangedJob.color }}
          >
            {gangedJob.clientName}
          </div>

          <div className="text-[9px] font-bold text-zinc-900 truncate w-full px-0.5">
            {gangedJob.jobName}
          </div>

          {viewSide === 'back' && (
            <div className="text-[7px] text-purple-700 font-mono bg-purple-100 px-1 rounded border border-purple-300">
              BACK SIDE MIRROR ({targetR + 1},{targetC + 1})
            </div>
          )}

          <div className="text-[8px] font-mono text-zinc-700 bg-zinc-100 px-1 rounded border border-zinc-200">
            Qty Req: {gangedJob.requiredQty}
          </div>
        </div>
      ) : (
        <>
          <span className="opacity-20 pointer-events-none">{getPreviewRecordIndex(r, c)}</span>
          {viewSide === 'back' && (
            <div className="text-[8px] font-mono text-purple-600 font-bold bg-purple-100 px-1 rounded border border-purple-300 mt-1">
              BACK ({targetR + 1},{targetC + 1})
            </div>
          )}
          {layout.bleed && layout.bleed > 0 && (
            <div className="absolute border border-dashed border-red-400 pointer-events-none" style={{
              left: `${-bleedVal}px`,
              top: `${-bleedVal}px`,
              right: `${-bleedVal}px`,
              bottom: `${-bleedVal}px`,
              zIndex: 20
            }}></div>
          )}

          {/* CutContour Spot Layer Vector Path */}
          {layout.enableCutContour && (
            <div 
              className="absolute pointer-events-none transition-all shadow-xs"
              style={{
                left: 0,
                top: 0,
                right: 0,
                bottom: 0,
                borderWidth: `${Math.max(1, (layout.cutContourStrokeWidth || 0.25) * previewScale)}px`,
                borderColor: layout.cutContourColor || '#FF00FF',
                borderStyle: 'solid',
                zIndex: 28
              }}
            >
              <span className="absolute -top-3 left-0 text-[8px] font-mono font-bold bg-pink-600 text-white px-1 py-0.5 rounded shadow-xs uppercase">
                {layout.cutContourName || 'CutContour'}
              </span>
            </div>
          )}

          {/* Dual Cut Line Vector Paths */}
          {layout.enableDualCutLine && (
            <>
              {/* Inner Kiss-Cut Line */}
              <div 
                className="absolute pointer-events-none transition-all"
                style={{
                  left: `${(layout.kissCutInset || 2) * previewScale}px`,
                  top: `${(layout.kissCutInset || 2) * previewScale}px`,
                  right: `${(layout.kissCutInset || 2) * previewScale}px`,
                  bottom: `${(layout.kissCutInset || 2) * previewScale}px`,
                  borderWidth: '1px',
                  borderColor: layout.kissCutColor || '#FF00FF',
                  borderStyle: 'dashed',
                  zIndex: 29
                }}
              >
                <span className="absolute bottom-0 right-0 text-[7px] font-mono text-pink-300 bg-pink-950/80 px-1 rounded">
                  Kiss-Cut
                </span>
              </div>

              {/* Outer Thru-Cut / Die-Cut Punch Line */}
              <div 
                className="absolute pointer-events-none transition-all"
                style={{
                  left: 0,
                  top: 0,
                  right: 0,
                  bottom: 0,
                  borderWidth: '1.5px',
                  borderColor: layout.thruCutColor || '#00FFFF',
                  borderStyle: 'solid',
                  zIndex: 30
                }}
              >
                <span className="absolute top-0 right-0 text-[7px] font-mono text-emerald-300 bg-emerald-950/80 px-1 rounded">
                  Thru-Cut
                </span>
              </div>
            </>
          )}

          {/* Render Template Elements */}
          <div 
            className="absolute inset-0 pointer-events-none overflow-hidden"
            style={{
               backgroundColor: template.backgroundColor || 'transparent',
               transform: `scale(${previewScale}) ${(!layout.isRollMode && layout.rotateStickers) ? 'rotate(90deg) translateY(-100%)' : ''}`,
               transformOrigin: 'top left',
               width: `${template.width}px`,
               height: `${template.height}px`,
               borderRadius: template.shape === 'circle' || template.shape === 'oval' ? '50%' : `${template.cornerRadius || 0}px`,
               overflow: 'hidden'
            }}
          >
      {template.elements.map(el => {
         // Get actual record data based on index
         const slotKey = `${r}-${c}`;
         if (disabledSlots.includes(slotKey)) return null;

         const localIdx = (getPreviewRecordIndex as any).__slotDataIndices?.[slotKey] ?? 0;
         const prevPagesSlots = (previewPage - 1) * (stickersPerPage || (rows * cols));

         let dataIndex;
         if (layout.nestingDirection === 'cut_stack') {
           dataIndex = localIdx * pagesNeeded + (previewPage - 1);
         } else {
           dataIndex = localIdx + prevPagesSlots;
         }
         
         const recordNumber = layout.nestingMode === 'step_and_repeat' ? Math.floor(dataIndex / copies) : dataIndex;
         const rowCount = csvData?.rows?.length || 0;
         const effectiveRecordNum = rowCount > 0 ? (recordNumber % rowCount) : 0;
         const fallbackText = el.content ?? el.text ?? el.staticValue ?? el.value ?? el.name ?? '';
         let previewValue = fallbackText;

         if (el.columnName && el.columnName.trim() !== '') {
           const colName = el.columnName.trim();
           if (csvData && csvData.rows && rowCount > 0 && csvData.rows[effectiveRecordNum]) {
             const csvVal = csvData.rows[effectiveRecordNum][colName];
             if (csvVal !== undefined && csvVal !== null && String(csvVal).trim() !== '') {
               previewValue = String(csvVal);
             } else if (!fallbackText) {
               previewValue = `[${colName}]`;
             }
           } else if (!fallbackText) {
             previewValue = `[${colName}]`;
           }
         }

         if (el.type === 'image') {
           if (el.columnName && csvData && csvData.rows && rowCount > 0 && csvData.rows[effectiveRecordNum] && csvData.rows[effectiveRecordNum][el.columnName]) {
             previewValue = csvData.rows[effectiveRecordNum][el.columnName];
           } else {
             previewValue = el.src || el.staticValue || el.content || '';
           }
         }
         
         // Apply casing
         if (['text', 'textbox', 'currency', 'date', 'serial', 'uppercase'].includes(el.type) && typeof previewValue === 'string') {
           if (el.textCase === 'uppercase' || el.type === 'uppercase') {
             previewValue = previewValue.toUpperCase();
           } else if (el.textCase === 'lowercase') {
             previewValue = previewValue.toLowerCase();
           } else if (el.textCase === 'capitalize') {
             previewValue = previewValue.replace(/\b\w/g, char => char.toUpperCase());
           }
         }

         // Apply prefix / suffix
         if (['text', 'textbox', 'currency', 'date', 'serial', 'uppercase'].includes(el.type)) {
           if (el.prefix) previewValue = `${el.prefix}${previewValue}`;
           if (el.suffix) previewValue = `${previewValue}${el.suffix}`;
         }

         return (
           <div
             key={el.id}
             className="absolute select-none"
             style={{
               left: `${el.x}px`,
               top: `${el.y}px`,
               width: el.width ? `${el.width}px` : 'auto',
               height: el.height ? `${el.height}px` : 'auto',
               zIndex: el.zIndex || 0,
               transform: `rotate(${el.rotation || 0}deg)`,
               transformOrigin: 'center center',
               overflow: el.autoShrink ? 'hidden' : 'visible',
               opacity: el.opacity !== undefined && !isNaN(Number(el.opacity)) ? Number(el.opacity) : 1,
               borderRadius: el.type === 'circle' ? '50%' : ((el.borderRadius ?? el.cornerRadius) ? `${el.borderRadius ?? el.cornerRadius}px` : undefined),
               backgroundColor: (el.type !== 'path' && el.type !== 'pen' && el.backgroundColor && !el.isTransparentBg && el.backgroundColor !== 'transparent' && el.backgroundColor !== 'none') ? el.backgroundColor : undefined,
               border: (el.type !== 'rectangle' && el.type !== 'circle' && el.borderWidth && el.borderColor) ? `${el.borderWidth}px solid ${el.borderColor}` : undefined,
               boxSizing: 'border-box'
             }}
           >
             {['text', 'textbox', 'uppercase', 'currency', 'date', 'serial'].includes(el.type) && (() => {
               let displayValue = previewValue;
               if (el.type === 'currency') {
                displayValue = formatCurrencyValue(previewValue, el.currencySymbol || '₹', el.decimalPlaces ?? 2, el.formatThousands ?? true);
              } else if (el.type === 'date') {
                displayValue = formatDateValue(previewValue, el.dateFormat || 'DD/MM/YYYY');
              } else if (el.type === 'serial') {
                const prefix = el.serialPrefix !== undefined ? el.serialPrefix : 'SN-';
                const step = el.serialStep || 1;
                const currentRec = layout.nestingMode === 'step_and_repeat' ? Math.floor(dataIndex / copies) : dataIndex;
                const startNum = (el.serialStart || 1) + (currentRec * step);
                const padding = el.serialPadding || 4;
                displayValue = `${prefix}${String(startNum).padStart(padding, '0')}`;
              }
               
               return (
                 <div
                  style={{
                    width: '100%',
                    height: '100%',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: el.textAlign === 'center' ? 'center' : (el.textAlign === 'right' ? 'flex-end' : 'flex-start')
                  }}
                 >
                   <span 
                     style={{ 
                       textDecoration: el.textDecoration || 'none',
                       fontVariant: el.textVariant === 'small-caps' ? 'small-caps' : 'normal',
                       verticalAlign: el.verticalAlign || 'baseline',
                       fontSize: `${el.fontSize || 14}px`, 
                       color: el.color || '#000', 
                       lineHeight: el.lineHeight || 1.2, 
                       whiteSpace: 'nowrap', 
                       textTransform: el.type === 'uppercase' ? 'uppercase' : 'none', 
                       fontFamily: el.fontFamily || (el.type === 'serial' ? 'monospace' : 'Helvetica'), 
                       fontWeight: el.isBold || el.fontWeight === 'bold' ? 'bold' : 'normal',
                       fontStyle: el.isItalic || el.fontStyle === 'italic' ? 'italic' : 'normal',
                       letterSpacing: el.letterSpacing ? `${el.letterSpacing}px` : 'normal',
                       transform: `scaleX(${el.horizontalScale !== undefined ? el.horizontalScale / 100 : 1}) scaleY(${el.verticalScale !== undefined ? el.verticalScale / 100 : 1}) rotate(${el.textRotation || 0}deg) translateY(${-(el.baselineShift || 0)}px)`,
                       display: 'inline-block',
                       transformOrigin: 'center center',
                       WebkitTextStroke: el.borderWidth ? `${el.borderWidth}px ${el.borderColor || '#000'}` : undefined,
                     }}
                   >
                     {displayValue}
                   </span>
                 </div>
               );
             })()}
             {el.type === 'rectangle' && (
               <div 
                 style={{ 
                   width: '100%', 
                   height: '100%', 
                   backgroundColor: (el.fillColor && el.fillColor !== 'none') ? el.fillColor : (el.color && el.color !== 'none') ? el.color : (el.color === 'none' || el.fillColor === 'none' ? 'transparent' : '#e2e8f0'), 
                   borderColor: el.borderColor || el.strokeColor || '#94a3b8',
                   borderWidth: `${el.borderWidth ?? el.strokeWidth ?? 0}px`,
                   borderStyle: (el.borderWidth || el.strokeWidth) ? 'solid' : 'none',
                   borderRadius: `${el.borderRadius ?? el.cornerRadius ?? 0}px`,
                   boxSizing: 'border-box'
                 }}
               />
             )}
             {el.type === 'circle' && (
               <div 
                 style={{ 
                   width: '100%', 
                   height: '100%', 
                   backgroundColor: (el.fillColor && el.fillColor !== 'none') ? el.fillColor : (el.color && el.color !== 'none') ? el.color : (el.color === 'none' || el.fillColor === 'none' ? 'transparent' : '#e2e8f0'), 
                   borderRadius: '50%', 
                   borderColor: el.borderColor || el.strokeColor || '#94a3b8',
                   borderWidth: `${el.borderWidth ?? el.strokeWidth ?? 0}px`,
                   borderStyle: (el.borderWidth || el.strokeWidth) ? 'solid' : 'none',
                   boxSizing: 'border-box'
                 }}
               />
             )}
             {el.type === 'triangle' && (
               <svg width="100%" height="100%" viewBox={`0 0 ${el.width || 100} ${el.height || 100}`}>
                 <path 
                   d={getTriangleSvgPath(el.width || 100, el.height || 100)} 
                   fill={(el.fillColor && el.fillColor !== 'none') ? el.fillColor : (el.color && el.color !== 'none') ? el.color : '#3b82f6'} 
                   stroke={el.borderColor || el.strokeColor || '#1d4ed8'} 
                   strokeWidth={el.borderWidth ?? el.strokeWidth ?? 0}
                   strokeDasharray={getStrokeDasharray(el.lineDash, el.borderWidth ?? el.strokeWidth ?? 2)}
                   strokeLinecap={el.lineCap || 'round'}
                   strokeLinejoin={el.lineJoin || 'miter'}
                 />
               </svg>
             )}
             {el.type === 'diamond' && (
               <svg width="100%" height="100%" viewBox={`0 0 ${el.width || 100} ${el.height || 100}`}>
                 <path 
                   d={getDiamondSvgPath(el.width || 100, el.height || 100)} 
                   fill={(el.fillColor && el.fillColor !== 'none') ? el.fillColor : (el.color && el.color !== 'none') ? el.color : '#3b82f6'} 
                   stroke={el.borderColor || el.strokeColor || '#1d4ed8'} 
                   strokeWidth={el.borderWidth ?? el.strokeWidth ?? 0}
                   strokeDasharray={getStrokeDasharray(el.lineDash, el.borderWidth ?? el.strokeWidth ?? 2)}
                   strokeLinecap={el.lineCap || 'round'}
                   strokeLinejoin={el.lineJoin || 'miter'}
                 />
               </svg>
             )}
             {el.type === 'heart' && (
               <svg width="100%" height="100%" viewBox={`0 0 ${el.width || 100} ${el.height || 100}`}>
                 <path 
                   d={getHeartSvgPath(el.width || 100, el.height || 100)} 
                   fill={(el.fillColor && el.fillColor !== 'none') ? el.fillColor : (el.color && el.color !== 'none') ? el.color : '#ec4899'} 
                   stroke={el.borderColor || el.strokeColor || '#be185d'} 
                   strokeWidth={el.borderWidth ?? el.strokeWidth ?? 0}
                   strokeDasharray={getStrokeDasharray(el.lineDash, el.borderWidth ?? el.strokeWidth ?? 2)}
                   strokeLinecap={el.lineCap || 'round'}
                   strokeLinejoin={el.lineJoin || 'miter'}
                 />
               </svg>
             )}
             {el.type === 'arrow' && (
               <svg width="100%" height="100%" viewBox={`0 0 ${el.width || 100} ${el.height || 100}`}>
                 <path 
                   d={getArrowSvgPath(el.width || 100, el.height || 100)} 
                   fill={(el.fillColor && el.fillColor !== 'none') ? el.fillColor : (el.color && el.color !== 'none') ? el.color : '#3b82f6'} 
                   stroke={el.borderColor || el.strokeColor || '#1d4ed8'} 
                   strokeWidth={el.borderWidth ?? el.strokeWidth ?? 0}
                   strokeDasharray={getStrokeDasharray(el.lineDash, el.borderWidth ?? el.strokeWidth ?? 2)}
                   strokeLinecap={el.lineCap || 'round'}
                   strokeLinejoin={el.lineJoin || 'miter'}
                 />
               </svg>
             )}
             {el.type === 'image' && (
               el.src || el.staticValue ? (
                 <img src={el.src || el.staticValue} alt="Sticker Element" className={el.objectFit === 'cover' ? 'object-cover' : el.objectFit === 'stretch' ? 'object-fill' : 'object-contain'} style={{ width: '100%', height: '100%', filter: (el.borderWidth && el.borderColor && el.borderColor !== 'transparent' ? `drop-shadow(${el.borderWidth}px 0 0 ${el.borderColor}) drop-shadow(-${el.borderWidth}px 0 0 ${el.borderColor}) drop-shadow(0 ${el.borderWidth}px 0 ${el.borderColor}) drop-shadow(0 -${el.borderWidth}px 0 ${el.borderColor})` : undefined) }} />
               ) : (
                 <div style={{ width: '100%', height: '100%', backgroundColor: '#f1f5f9', border: '1px solid #cbd5e1' }}></div>
               )
             )}
             {el.type === 'qr' && (
               <div style={{ width: '100%', height: '100%', backgroundColor: el.isTransparentBg ? 'transparent' : (el.backgroundColor || '#ffffff'), border: el.borderWidth ? `${el.borderWidth}px solid ${el.borderColor || '#000'}` : 'none', borderRadius: el.borderRadius ? `${el.borderRadius}px` : undefined, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                 <QrCodeSvg
                   value={String(previewValue || 'https://example.com')}
                   color={el.color || '#000000'}
                   backgroundColor={el.backgroundColor || '#ffffff'}
                   isTransparentBg={el.isTransparentBg}
                   errorCorrection={el.qrErrorCorrection || 'M'}
                   style={el.qrStyle || 'square'}
                   width={el.width || 100}
                   height={el.height || 100}
                 />
               </div>
             )}
             {el.type === 'datamatrix' && (
               <div style={{ width: '100%', height: '100%', backgroundColor: el.isTransparentBg ? 'transparent' : (el.backgroundColor || '#ffffff'), border: el.borderWidth ? `${el.borderWidth}px solid ${el.borderColor || '#000'}` : 'none', borderRadius: el.borderRadius ? `${el.borderRadius}px` : undefined, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                 <DataMatrixSvg
                   value={String(previewValue || 'DATAMATRIX')}
                   color={el.color || '#000000'}
                   backgroundColor={el.backgroundColor || '#ffffff'}
                   isTransparentBg={el.isTransparentBg}
                   width={el.width || 80}
                   height={el.height || 80}
                 />
               </div>
             )}
             {(el.type === 'barcode' || el.type === 'ean13' || el.type === 'upca') && (
               <div style={{ width: '100%', height: '100%', backgroundColor: el.isTransparentBg ? 'transparent' : (el.backgroundColor || '#ffffff'), border: el.borderWidth ? `${el.borderWidth}px solid ${el.borderColor || '#000'}` : 'none', borderRadius: el.borderRadius ? `${el.borderRadius}px` : undefined, display: 'flex', alignItems: 'center', justifyContent: 'center', overflow: 'hidden' }}>
                 {el.type === 'ean13' ? (
                   <Ean13BarcodeSvg
                     value={String(previewValue || '5901234123457')}
                     color={el.color || '#000000'}
                     backgroundColor={el.backgroundColor || '#ffffff'}
                     isTransparentBg={el.isTransparentBg}
                     showText={el.showBarcodeText !== false}
                     width={el.width || 160}
                     height={el.height || 80}
                   />
                 ) : el.type === 'upca' ? (
                   <UpcaBarcodeSvg
                     value={String(previewValue || '012345678905')}
                     color={el.color || '#000000'}
                     backgroundColor={el.backgroundColor || '#ffffff'}
                     isTransparentBg={el.isTransparentBg}
                     showText={el.showBarcodeText !== false}
                     width={el.width || 160}
                     height={el.height || 80}
                   />
                 ) : (
                   <ReactBarcode
                     value={String(previewValue || 'BARCODE')}
                     width={1.2}
                     height={Math.max(20, (el.height || 60) - 15)}
                     fontSize={10}
                     lineColor={el.color || '#000000'}
                     background={el.isTransparentBg ? 'rgba(0,0,0,0)' : (el.backgroundColor || '#ffffff')}
                     displayValue={el.showBarcodeText !== false}
                     margin={0}
                   />
                 )}
               </div>
             )}
             {el.type === 'star' && (
               <svg width="100%" height="100%" viewBox={`0 0 ${el.width || 100} ${el.height || 100}`}>
                 <path 
                   d={getStarSvgPath(el.width || 100, el.height || 100, el.starPoints || 5, el.starInnerRadiusRatio || 0.4, el.cornerRadius ?? el.borderRadius ?? 0)} 
                   fill={(el.fillColor && el.fillColor !== 'none') ? el.fillColor : (el.color && el.color !== 'none') ? el.color : '#f59e0b'} 
                   stroke={el.borderColor || el.strokeColor || '#d97706'} 
                   strokeWidth={el.borderWidth ?? el.strokeWidth ?? 0}
                   strokeDasharray={getStrokeDasharray(el.lineDash, el.borderWidth ?? el.strokeWidth ?? 2)}
                   strokeLinecap={el.lineCap || 'round'}
                   strokeLinejoin={el.lineJoin || 'miter'}
                 />
               </svg>
             )}
             {el.type === 'polygon' && (
               <svg width="100%" height="100%" viewBox={`0 0 ${el.width || 100} ${el.height || 100}`}>
                 <path 
                   d={getPolygonSvgPath(el.width || 100, el.height || 100, el.polygonSides || 6, el.cornerRadius ?? el.borderRadius ?? 0)} 
                   fill={(el.fillColor && el.fillColor !== 'none') ? el.fillColor : (el.color && el.color !== 'none') ? el.color : '#3b82f6'} 
                   stroke={el.borderColor || el.strokeColor || '#1d4ed8'} 
                   strokeWidth={el.borderWidth ?? el.strokeWidth ?? 0}
                   strokeDasharray={getStrokeDasharray(el.lineDash, el.borderWidth ?? el.strokeWidth ?? 2)}
                   strokeLinecap={el.lineCap || 'round'}
                   strokeLinejoin={el.lineJoin || 'miter'}
                 />
               </svg>
             )}
             {el.type === 'line' && (
               <svg width="100%" height="100%" className="overflow-visible">
                 {renderArrowheadDefs(el)}
                 <path 
                   d={getLineEffectSvgPath(el.width || 100, el.height || 20, el.lineEffect || 'none', el.effectAmplitude, el.effectFrequency, el.effectPointsStyle)} 
                   fill="none" 
                   stroke={el.borderColor || el.strokeColor || '#000000'} 
                   strokeWidth={el.borderWidth ?? el.strokeWidth ?? 2}
                   strokeDasharray={getStrokeDasharray(el.lineDash, el.borderWidth ?? el.strokeWidth ?? 2)}
                   strokeLinecap={el.lineCap || 'round'}
                   strokeLinejoin={el.lineJoin || 'miter'}
                   markerStart={el.arrowheadStart && el.arrowheadStart !== 'none' ? `url(#arrow-start-${el.id})` : undefined}
                   markerEnd={el.arrowheadEnd && el.arrowheadEnd !== 'none' ? `url(#arrow-end-${el.id})` : undefined}
                 />
               </svg>
             )}
             {(el.type === 'path' || el.type === 'pen') && (
               <svg width="100%" height="100%" className="overflow-visible">
                 {renderArrowheadDefs(el)}
                 <path 
                   d={el.pathData || el.pathD || (el.points ? pointsToSvgPath(el.points, el.closedPath, el.isSmooth) : '')} 
                   fill={(el.fillColor && el.fillColor !== 'none') ? el.fillColor : (el.backgroundColor && el.backgroundColor !== 'none' && !el.isTransparentBg) ? el.backgroundColor : (el.color && el.color !== 'none') ? el.color : 'none'} 
                   fillRule={el.fillRule || 'nonzero'}
                   stroke={el.borderColor || el.strokeColor || '#2563eb'} 
                   strokeWidth={el.borderWidth ?? el.strokeWidth ?? 2}
                   strokeDasharray={getStrokeDasharray(el.lineDash, el.borderWidth ?? el.strokeWidth ?? 2)}
                   strokeLinecap={el.lineCap || 'round'}
                   strokeLinejoin={el.lineJoin || 'miter'}
                   markerStart={el.arrowheadStart && el.arrowheadStart !== 'none' ? `url(#arrow-start-${el.id})` : undefined}
                   markerEnd={el.arrowheadEnd && el.arrowheadEnd !== 'none' ? `url(#arrow-end-${el.id})` : undefined}
                 />
               </svg>
             )}
           </div>
         );
      })}
    </div>

    {layout.showTrimMarks && layout.trimMarkType === 'individual' ? (
      <>
        {/* Top Left */}
        <div className="absolute h-px bg-black" style={{ width: `${effectiveLen}px`, left: `-${effectiveOffset + effectiveLen}px`, top: '0', zIndex: 10 }}></div>
        <div className="absolute w-px bg-black" style={{ height: `${effectiveLen}px`, left: '0', top: `-${effectiveOffset + effectiveLen}px`, zIndex: 10 }}></div>
        
        {/* Top Right */}
        <div className="absolute h-px bg-black" style={{ width: `${effectiveLen}px`, right: `-${effectiveOffset + effectiveLen}px`, top: '0', zIndex: 10 }}></div>
        <div className="absolute w-px bg-black" style={{ height: `${effectiveLen}px`, right: '0', top: `-${effectiveOffset + effectiveLen}px`, zIndex: 10 }}></div>

        {/* Bottom Left */}
        <div className="absolute h-px bg-black" style={{ width: `${effectiveLen}px`, left: `-${effectiveOffset + effectiveLen}px`, bottom: '0', zIndex: 10 }}></div>
        <div className="absolute w-px bg-black" style={{ height: `${effectiveLen}px`, left: '0', bottom: `-${effectiveOffset + effectiveLen}px`, zIndex: 10 }}></div>

        {/* Bottom Right */}
        <div className="absolute h-px bg-black" style={{ width: `${effectiveLen}px`, right: `-${effectiveOffset + effectiveLen}px`, bottom: '0', zIndex: 10 }}></div>
        <div className="absolute w-px bg-black" style={{ height: `${effectiveLen}px`, right: '0', bottom: `-${effectiveOffset + effectiveLen}px`, zIndex: 10 }}></div>
      </>
    ) : null}
        </>
      )}
      </div>
    </div>
  );
});

interface ImpositionPreviewProps {
  onToggleDrawer?: () => void;
  isDrawerOpen?: boolean;
  onToggleFloatingThumbnail?: () => void;
  showFloatingThumbnail?: boolean;
}

export const ImpositionPreview: React.FC<ImpositionPreviewProps> = ({
  onToggleDrawer,
  isDrawerOpen,
  onToggleFloatingThumbnail,
  showFloatingThumbnail
}) => {
  const { layout, setLayout, template, csvData, appUnit = 'mm' } = useSaaSContext();
  const threeD = useThreeDContext();
  const { toast } = useToast();
  const [zoomLevel, setZoomLevel] = useState(1);
  const [previewPage, setPreviewPage] = useState(1);
  const [slotInteractionMode, setSlotInteractionMode] = useState<'preview' | 'skip_toggle' | 'hand'>('preview');
  const [showLayoutOverlay, setShowLayoutOverlay] = useState(true);
  const [showTrimMediaModal, setShowTrimMediaModal] = useState(false);
  const [showColorBarsModal, setShowColorBarsModal] = useState(false);
  const [showSafeZoneModal, setShowSafeZoneModal] = useState(false);
  const [showPlotterModal, setShowPlotterModal] = useState(false);
  const [showGangingModal, setShowGangingModal] = useState(false);
  const [show3DProofModal, setShow3DProofModal] = useState(false);
  const [showCoordinateDebugger, setShowCoordinateDebugger] = useState(false);
  const [coordinateMode, setCoordinateMode] = useState<'screen' | 'print_pt' | 'print_mm'>('screen');
  const [debugGridStepMm, setDebugGridStepMm] = useState<number>(10);
  const [debugHoverCoords, setDebugHoverCoords] = useState<{ x: number; y: number } | null>(null);

  const [panOffset, setPanOffset] = useState({ x: 0, y: 0 });
  const [isDragging, setIsDragging] = useState(false);
  const dragStart = useRef({ x: 0, y: 0, panX: 0, panY: 0 });
  const containerRef = useRef<HTMLDivElement>(null);

  // Canvas background style mode: default to dotted_light (reference image style)
  const [canvasBgMode, setCanvasBgMode] = useState<'dotted_light' | 'dotted_dark' | 'solid_dark'>(() => {
    try {
      const saved = localStorage.getItem('vdp_imposition_canvas_bg');
      if (saved === 'dotted_light' || saved === 'dotted_dark' || saved === 'solid_dark') {
        return saved;
      }
    } catch {}
    return 'dotted_light';
  });

  const handleToggleCanvasBg = useCallback((mode: 'dotted_light' | 'dotted_dark' | 'solid_dark') => {
    setCanvasBgMode(mode);
    try {
      localStorage.setItem('vdp_imposition_canvas_bg', mode);
    } catch {}
  }, []);

  const canvasBgStyle = useMemo(() => {
    if (canvasBgMode === 'dotted_light') {
      return {
        backgroundColor: '#f1f5f9',
        backgroundImage: 'radial-gradient(circle, rgba(15, 23, 42, 0.45) 1.25px, transparent 1.25px)',
        backgroundSize: '20px 20px',
        backgroundPosition: `${panOffset.x}px ${panOffset.y}px`,
      };
    }
    if (canvasBgMode === 'dotted_dark') {
      return {
        backgroundColor: '#18181b',
        backgroundImage: 'radial-gradient(circle, rgba(161, 161, 170, 0.35) 1.25px, transparent 1.25px)',
        backgroundSize: '20px 20px',
        backgroundPosition: `${panOffset.x}px ${panOffset.y}px`,
      };
    }
    return {
      backgroundColor: '#27272a',
    };
  }, [canvasBgMode, panOffset.x, panOffset.y]);

  // Spacebar Hotkey to temporarily trigger Hand Tool (pan) and release to preview
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.code === 'Space' && !e.repeat) {
        const active = document.activeElement;
        if (active && (active.tagName === 'INPUT' || active.tagName === 'TEXTAREA' || active.tagName === 'SELECT')) {
          return;
        }
        e.preventDefault();
        setSlotInteractionMode('hand');
      }
    };

    const handleKeyUp = (e: KeyboardEvent) => {
      if (e.code === 'Space') {
        setSlotInteractionMode('preview');
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    window.addEventListener('keyup', handleKeyUp);
    return () => {
      window.removeEventListener('keydown', handleKeyDown);
      window.removeEventListener('keyup', handleKeyUp);
    };
  }, []);

  const handleMouseDown = (e: React.MouseEvent<HTMLDivElement>) => {
    // Allow panning with middle mouse button (button 1) or when Hand tool is active
    if (slotInteractionMode !== 'hand' && e.button !== 1) return;
    e.preventDefault();
    setIsDragging(true);
    dragStart.current = {
      x: e.clientX,
      y: e.clientY,
      panX: panOffset.x,
      panY: panOffset.y
    };
  };

  const animFrame = useRef<number | null>(null);

  const handleMouseMove = (e: React.MouseEvent<HTMLDivElement>) => {
    if (!isDragging || (slotInteractionMode !== 'hand' && e.buttons !== 4)) return; // 4 is middle mouse button
    e.preventDefault();
    if (animFrame.current !== null) return;

    const dx = e.clientX - dragStart.current.x;
    const dy = e.clientY - dragStart.current.y;
    
    animFrame.current = requestAnimationFrame(() => {
      setPanOffset({
        x: dragStart.current.panX + dx,
        y: dragStart.current.panY + dy
      });
      animFrame.current = null;
    });
  };

  const handleMouseUp = () => {
    setIsDragging(false);
    if (animFrame.current !== null) {
      cancelAnimationFrame(animFrame.current);
      animFrame.current = null;
    }
  };

  const handleTouchStart = (e: React.TouchEvent<HTMLDivElement>) => {
    if (slotInteractionMode !== 'hand') return;
    if (e.touches.length === 0) return;
    setIsDragging(true);
    const touch = e.touches[0];
    dragStart.current = {
      x: touch.clientX,
      y: touch.clientY,
      panX: panOffset.x,
      panY: panOffset.y
    };
  };

  const handleTouchMove = (e: React.TouchEvent<HTMLDivElement>) => {
    if (!isDragging || slotInteractionMode !== 'hand' || e.touches.length === 0) return;
    if (animFrame.current !== null) return;
    
    const touch = e.touches[0];
    const dx = touch.clientX - dragStart.current.x;
    const dy = touch.clientY - dragStart.current.y;
    
    animFrame.current = requestAnimationFrame(() => {
      setPanOffset({
        x: dragStart.current.panX + dx,
        y: dragStart.current.panY + dy
      });
      animFrame.current = null;
    });
  };

  const handleWheel = (e: React.WheelEvent<HTMLDivElement>) => {
    // If the user holds Ctrl or Cmd, it's a zoom gesture, skip it here
    if (e.ctrlKey || e.metaKey) return;
    
    // Prevent default scroll if it's over the container
    // However, React passive events might require us to let it be, but since overflow is hidden, it doesn't scroll anyway.
    
    setPanOffset(prev => ({
      x: prev.x - e.deltaX,
      y: prev.y - e.deltaY
    }));
  };

  const disabledSlots = layout.disabledSlots || [];

  const toggleSlotDisabled = useCallback((r: number, c: number) => {
    const slotKey = `${r}-${c}`;
    const exists = disabledSlots.includes(slotKey);
    const updated = exists ? disabledSlots.filter(s => s !== slotKey) : [...disabledSlots, slotKey];
    setLayout({ ...layout, disabledSlots: updated });
  }, [disabledSlots, layout, setLayout]);

  const clearDisabledSlots = () => {
    setLayout({ ...layout, disabledSlots: [] });
  };

  // Scaled for preview roughly. A4 is 595x842 pts
  const baseScale = layout.pageSize === 'A3' ? 0.4 : 0.5;
  const previewScale = baseScale * zoomLevel;
  
  const isSingleUp = layout.exportMode === 'single_pdf' || layout.exportMode === 'separate_pdfs';

  const rawSheetWidth = isSingleUp ? (Number(template.width) + Number(layout.marginX || 0) * 2) : (layout.pageSize === 'custom' && layout.pageWidth ? Number(layout.pageWidth) : (layout.pageSize === 'A3' ? 842 : 595));
  const rawSheetHeight = isSingleUp ? (Number(template.height) + Number(layout.marginY || 0) * 2) : (layout.pageSize === 'custom' && layout.pageHeight ? Number(layout.pageHeight) : (layout.pageSize === 'A3' ? 1190 : 842));
  
  const isLandscape = !isSingleUp && layout.pageOrientation === 'landscape';

  const rawStickerWidthForCalc = Number(template.width) * previewScale;
  const rawStickerHeightForCalc = Number(template.height) * previewScale;
  
  // Handle Roll Mode Unwind Rotation Override
  const getRollRotation = () => {
    if (!layout.isRollMode) return 0;
    switch(String(layout.unwindDirection)) {
      case '1': case '5': return 0;
      case '2': case '6': return 180;
      case '3': case '7': return -90;
      case '4': case '8': return 90;
      default: return 0;
    }
  };

  const rollRot = getRollRotation() as number;
  const isRollRotated = rollRot === 90 || rollRot === -90 || rollRot === 270 || rollRot === -270;

  const effStickerHeight = layout.isRollMode 
    ? (isRollRotated ? rawStickerWidthForCalc : rawStickerHeightForCalc)
    : (layout.rotateStickers ? rawStickerWidthForCalc : rawStickerHeightForCalc);
  const effGapY = Number(layout.gapY) * previewScale;

  const cols = layout.isRollMode ? (layout.trackCount || 1) : (isSingleUp ? 1 : (Number(layout.cols) || 1));
  const rows = layout.isRollMode ? 5 : (isSingleUp ? 1 : (Number(layout.rows) || 1)); // 5 rows for continuous roll preview

  const rollWidthPt = (layout.rollWidthMm || 108) * 2.83465; // Convert mm to pt
  const finalSheetWidth = layout.isRollMode ? rollWidthPt : (isLandscape ? rawSheetHeight : rawSheetWidth);
  
  // In roll mode, just make it tall enough for the preview rows
  const finalSheetHeight = layout.isRollMode ? ((Number(layout.marginY || 0) * 2 * previewScale) + (effStickerHeight + effGapY) * rows) / previewScale : (isLandscape ? rawSheetWidth : rawSheetHeight);

  const sheetWidth = finalSheetWidth * previewScale;
  const sheetHeight = finalSheetHeight * previewScale;

  const stickersPerPage = cols * rows;
  const disabledSlotsCount = disabledSlots.length;
  const effectiveSlotsPerPage = Math.max(1, stickersPerPage - disabledSlotsCount);
  const dataLength = csvData?.rows?.length || 1;
  const copies = layout.nestingMode === 'step_and_repeat' ? (layout.quantityPerRecord || stickersPerPage) : 1;
  const totalStickers = dataLength * copies;
  const pagesNeeded = Math.ceil(totalStickers / effectiveSlotsPerPage);

  // Reset to page 1 if layout changes
  useEffect(() => {
    setPreviewPage(1);
  }, [layout.nestingMode, layout.nestingDirection, layout.rows, layout.cols, copies, dataLength]);

  // Pre-calculate effective data indices for each slot on the current page to handle disabledSlots correctly
  const slotDataIndices = useMemo(() => {
    const map: Record<string, number> = {};
    let currentIdxOnPage = 0;
    
    // We need to iterate in the same order as nestingDirection
    const slots: {r: number, c: number}[] = [];
    if (layout.nestingDirection === 'col_first') {
      for (let c = 0; c < cols; c++) {
        for (let r = 0; r < rows; r++) {
          slots.push({ r, c });
        }
      }
    } else {
      for (let r = 0; r < rows; r++) {
        for (let c = 0; c < cols; c++) {
          slots.push({ r, c });
        }
      }
    }

    for (const slot of slots) {
      const key = `${slot.r}-${slot.c}`;
      if (!disabledSlots.includes(key)) {
        map[key] = currentIdxOnPage;
        currentIdxOnPage++;
      }
    }
    return map;
  }, [rows, cols, disabledSlots, layout.nestingDirection]);

  const getPreviewRecordIndex = useCallback((r: number, c: number) => {
    const slotKey = `${r}-${c}`;
    if (disabledSlots.includes(slotKey)) return 'Skipped';

    const localIdx = slotDataIndices[slotKey] ?? 0;
    const prevPagesSlots = (previewPage - 1) * effectiveSlotsPerPage;
    
    let dataIndex;
    if (layout.nestingDirection === 'cut_stack') {
      dataIndex = localIdx * pagesNeeded + (previewPage - 1);
    } else {
      dataIndex = localIdx + prevPagesSlots;
    }
    
    if (dataIndex >= totalStickers) return 'Empty';
    
    // Reverse map dataIndex back to record
    const recordNumber = layout.nestingMode === 'step_and_repeat' ? Math.floor(dataIndex / copies) + 1 : dataIndex + 1;
    return `Rec ${recordNumber}`;
  }, [disabledSlots, slotDataIndices, previewPage, effectiveSlotsPerPage, layout.nestingDirection, layout.nestingMode, pagesNeeded, totalStickers, copies]);

  // Expose slotDataIndices to child components via the callback ref for performance
  useEffect(() => {
    (getPreviewRecordIndex as any).__slotDataIndices = slotDataIndices;
  }, [getPreviewRecordIndex, slotDataIndices]);

  const rawStickerWidth = Number(template.width) * previewScale;
  const rawStickerHeight = Number(template.height) * previewScale;
  
  const stickerWidth = layout.isRollMode 
    ? (isRollRotated ? rawStickerHeight : rawStickerWidth)
    : (layout.rotateStickers ? rawStickerHeight : rawStickerWidth);
    
  const stickerHeight = layout.isRollMode 
    ? (isRollRotated ? rawStickerWidth : rawStickerHeight)
    : (layout.rotateStickers ? rawStickerWidth : rawStickerHeight);

  const stickerVisualRotation = layout.isRollMode ? rollRot : 0;

  const gapX = Number(layout.gapX) * previewScale;
  const gapY = Number(layout.gapY) * previewScale;
  const marginX = Number(layout.marginX) * previewScale;
  const marginY = Number(layout.marginY) * previewScale;
  const bleedVal = (layout.bleed || 0) * previewScale;
  const stepX = stickerWidth + bleedVal * 2 + gapX;
  const stepY = stickerHeight + bleedVal * 2 + gapY;

  const tmOffset = ((layout.trimMarkOffset !== undefined ? layout.trimMarkOffset : 8.5) * previewScale);
  const tmLen = ((layout.trimMarkLength !== undefined ? layout.trimMarkLength : 10) * previewScale);
  const effectiveOffset = Math.max(tmOffset, 2);
  const effectiveLen = Math.max(tmLen, 4);

  // Calculate precise cut coordinates relative to the margin container with memoization for heavy grids
  const { cutXs, cutYs, minX, maxX, minY, maxY } = useMemo(() => {
    const xs: number[] = [];
    const ys: number[] = [];
    for (let c = 0; c < cols; c++) {
      const leftX = c * stepX + bleedVal;
      const rightX = c * stepX + bleedVal + stickerWidth;
      if (!xs.includes(leftX)) xs.push(leftX);
      if (!xs.includes(rightX)) xs.push(rightX);
    }
    for (let r = 0; r < rows; r++) {
      const topY = r * stepY + bleedVal;
      const bottomY = r * stepY + bleedVal + stickerHeight;
      if (!ys.includes(topY)) ys.push(topY);
      if (!ys.includes(bottomY)) ys.push(bottomY);
    }
    xs.sort((a, b) => a - b);
    ys.sort((a, b) => a - b);

    const min_X = xs.length > 0 ? xs[0] : bleedVal;
    const max_X = xs.length > 0 ? xs[xs.length - 1] : (cols * stepX - gapX - bleedVal);
    const min_Y = ys.length > 0 ? ys[0] : bleedVal;
    const max_Y = ys.length > 0 ? ys[ys.length - 1] : (rows * stepY - gapY - bleedVal);

    return { cutXs: xs, cutYs: ys, minX: min_X, maxX: max_X, minY: min_Y, maxY: max_Y };
  }, [cols, rows, stepX, stepY, bleedVal, stickerWidth, stickerHeight, gapX, gapY]);

  // Phase 3: Ganging & Work-and-Turn Mappings
  const isGangingActive = Boolean(layout.enableGanging);
  const gangingJobsList = layout.gangingJobs && layout.gangingJobs.length > 0 ? layout.gangingJobs : DEFAULT_GANGING_JOBS;
  const { gangingSlotMap, gangingError } = useMemo(() => {
    if (!isGangingActive) return { gangingSlotMap: {}, gangingError: null };
    try {
      const map = mapGridSlotsToGangingJobs(gangingJobsList, rows, cols, layout.gangingStrategy || 'quantity_balanced', layout.disabledSlots || []);
      return { gangingSlotMap: map, gangingError: null };
    } catch (err: any) {
      return { gangingSlotMap: {}, gangingError: err?.message || 'Ganging slot allocation error' };
    }
  }, [isGangingActive, gangingJobsList, rows, cols, layout.gangingStrategy]);

  const viewSide = layout.viewSide || 'front';
  const impositionType = layout.impositionType || 'single_sided';

  // Calculate paper area utilization efficiency
  const efficiencyPercent = useMemo(() => {
    const totalSheetArea = finalSheetWidth * finalSheetHeight;
    if (!totalSheetArea) return 0;
    const stickerTrimArea = Number(template.width) * Number(template.height) * (rows * cols);
    return Math.min(100, Math.max(0, Number(((stickerTrimArea / totalSheetArea) * 100).toFixed(1))));
  }, [finalSheetWidth, finalSheetHeight, template.width, template.height, rows, cols]);

  // Auto-fit zoom to sheet height/width
  const handleFitToPage = () => {
    if (containerRef.current) {
      const { clientWidth, clientHeight } = containerRef.current;
      const margin = 48;
      const availW = Math.max(100, clientWidth - margin);
      const availH = Math.max(100, clientHeight - margin);

      const scaleX = availW / (finalSheetWidth * baseScale);
      const scaleY = availH / (finalSheetHeight * baseScale);
      const fittedZoom = Math.min(scaleX, scaleY);

      if (fittedZoom > 0 && !isNaN(fittedZoom)) {
        setZoomLevel(Number(Math.min(5, Math.max(0.15, fittedZoom)).toFixed(2)));
      }
      setPanOffset({ x: 0, y: 0 });
    }
  };

  useEffect(() => {
    // Initial auto-fit or size change fit
    const timer = setTimeout(() => {
      handleFitToPage();
    }, 50);
    return () => clearTimeout(timer);
  }, [finalSheetWidth, finalSheetHeight, baseScale]);

  useEffect(() => {
    if (!containerRef.current) return;
    const observer = new ResizeObserver(() => {
      // Keep fit to page responsive
    });
    observer.observe(containerRef.current);
    return () => observer.disconnect();
  }, []);

  const handleAutoCalculate = () => {
    const availW = finalSheetWidth - ((Number(layout.marginX) || 0) * 2);
    const availH = finalSheetHeight - ((Number(layout.marginY) || 0) * 2);
    
    const bld = layout.bleed || 0;
    const wWithBleed = template.width + (bld * 2);
    const hWithBleed = template.height + (bld * 2);
    
    const gapX = Number(layout.gapX) || 0;
    const gapY = Number(layout.gapY) || 0;
    
    // Normal Orientation
    const newCols1 = Math.floor((availW + gapX) / (wWithBleed + gapX));
    const newRows1 = Math.floor((availH + gapY) / (hWithBleed + gapY));
    const total1 = Math.max(0, newCols1) * Math.max(0, newRows1);
    
    // Rotated 90 Orientation
    const newCols2 = Math.floor((availW + gapX) / (hWithBleed + gapX));
    const newRows2 = Math.floor((availH + gapY) / (wWithBleed + gapY));
    const total2 = Math.max(0, newCols2) * Math.max(0, newRows2);
    
    if (total1 === 0 && total2 === 0) {
      toast.error("Auto-Fit Error", "Template size plus margins is too large to fit even one on the sheet.");
      return;
    }
    
    // Auto-Nest Optimizer
    if (total2 > total1) {
      setLayout(prev => ({ ...prev, cols: newCols2, rows: newRows2, rotateStickers: true }));
      toast.success("Layout Optimized! ✨", `Rotated stickers to fit ${total2} per sheet (was ${total1}).`);
    } else {
      setLayout(prev => ({ ...prev, cols: newCols1, rows: newRows1, rotateStickers: false }));
      toast.success("Already Optimal ✨", `Current layout is already the most efficient (${total1} per sheet).`);
    }
  };

  const handleCenterOnPage = () => {
    const bld = layout.bleed || 0;
    const wWithBleed = template.width + (bld * 2);
    const hWithBleed = template.height + (bld * 2);
    const effW = layout.rotateStickers ? hWithBleed : wWithBleed;
    const effH = layout.rotateStickers ? wWithBleed : hWithBleed;
    
    const curCols = Number(layout.cols) || 1;
    const curRows = Number(layout.rows) || 1;
    const gapX = Number(layout.gapX) || 0;
    const gapY = Number(layout.gapY) || 0;
    
    const totalGridW = (curCols * effW) + ((curCols - 1) * gapX);
    const totalGridH = (curRows * effH) + ((curRows - 1) * gapY);
    
    const newMarginX = Math.max(0, (finalSheetWidth - totalGridW) / 2);
    const newMarginY = Math.max(0, (finalSheetHeight - totalGridH) / 2);
    
    setLayout(prev => ({
      ...prev,
      marginX: newMarginX,
      marginY: newMarginY
    }));
    toast.success("Centered! ✨", "Equal margins applied to center grid on the page.");
  };

  return (
    <div className="w-full h-full flex-1 min-h-0 bg-zinc-800 rounded-xl border border-zinc-700/80 shadow-inner flex flex-col overflow-hidden relative">
      {/* Workspace Controls Header Bar - Single Unified Row with Icons & Tooltips */}
      <div id="tour-imposition-toolbar" className="w-full bg-zinc-900 border-b border-zinc-800 px-2.5 py-1 flex flex-nowrap items-center justify-between gap-1.5 z-20 shadow-sm shrink-0 overflow-x-auto custom-scrollbar min-h-[40px]">
        <div className="flex items-center gap-1.5 shrink-0">
          
          {/* Properties Drawer & Floating Thumbnail Toggles */}
          {onToggleDrawer && (
            <button 
              onClick={onToggleDrawer}
              className={`p-1.5 rounded-lg border transition-all cursor-pointer shrink-0 ${
                isDrawerOpen 
                  ? 'bg-blue-600 text-white border-blue-400 shadow-xs' 
                  : 'bg-zinc-800/90 hover:bg-zinc-700 text-zinc-300 border-zinc-700'
              }`}
              title={isDrawerOpen ? 'Close Properties Panel' : 'Open Imposition Properties Panel (Presets & Page Setup)'}
            >
              <SlidersHorizontal className="w-3.5 h-3.5" />
            </button>
          )}

          {onToggleFloatingThumbnail && (
            <button 
              onClick={onToggleFloatingThumbnail}
              className={`p-1.5 rounded-lg border transition-all cursor-pointer shrink-0 ${
                showFloatingThumbnail
                  ? 'bg-purple-600 hover:bg-purple-500 text-white border-purple-400/40 shadow-xs'
                  : 'bg-zinc-800/90 hover:bg-zinc-700 text-zinc-300 border-zinc-700'
              }`}
              title="Toggle Live Interactive Imposition Grid Thumbnail Window"
            >
              <Layers className="w-3.5 h-3.5 text-purple-300" />
            </button>
          )}

          <div className="w-px h-4 bg-zinc-800 shrink-0"></div>

          {/* Compact ROI & Paper Savings Group */}
          <RoiCalculatorPanel 
            compact={true}
            layout={layout}
            template={template}
            csvData={csvData}
            appUnit={appUnit}
            finalSheetWidth={finalSheetWidth}
            finalSheetHeight={finalSheetHeight}
            cols={cols}
            rows={rows}
          />

          <div className="w-px h-4 bg-zinc-800 shrink-0"></div>

          {/* Grid Quick Geometry Controls */}
          <div id="tour-imposition-grid-controls" className="flex items-center gap-1.5 shrink-0">
            {/* Grid Quick Geometry: Rows & Columns */}
            <div className="flex items-center gap-1 bg-zinc-950/80 px-1.5 py-0.5 rounded-md border border-zinc-800 shrink-0 h-7" title="Imposition Grid: Rows (R) and Columns (C)">
              <Grid3x3 className="w-3.5 h-3.5 text-zinc-400 shrink-0" />
              <input 
                type="number" min="1" max="100" placeholder="R"
                className="w-10 min-w-[38px] h-5.5 px-1 bg-zinc-800/90 border border-zinc-700 text-zinc-100 rounded text-[11px] font-mono text-center outline-none focus:ring-1 focus:ring-blue-500"
                value={layout.rows || ''}
                onChange={(e) => setLayout(prev => ({ ...prev, rows: e.target.value === '' ? '' : parseInt(e.target.value) }))}
                title="Grid Rows (R)"
              />
              <span className="text-[10px] text-zinc-500">×</span>
              <input 
                type="number" min="1" max="100" placeholder="C"
                className="w-10 min-w-[38px] h-5.5 px-1 bg-zinc-800/90 border border-zinc-700 text-zinc-100 rounded text-[11px] font-mono text-center outline-none focus:ring-1 focus:ring-blue-500"
                value={layout.cols || ''}
                onChange={(e) => setLayout(prev => ({ ...prev, cols: e.target.value === '' ? '' : parseInt(e.target.value) }))}
                title="Grid Columns (C)"
              />
            </div>

            {/* Gaps: Gx and Gy */}
            <div className="flex items-center gap-1 bg-zinc-950/80 px-1.5 py-0.5 rounded-md border border-zinc-800 shrink-0 h-7" title={`Sticker Spacing Gap X & Y in ${appUnit}`}>
              <Space className="w-3.5 h-3.5 text-zinc-400 shrink-0" />
              <input 
                type="number" min="0" placeholder="X"
                className="w-12 min-w-[46px] h-5.5 px-1 bg-zinc-800/90 border border-zinc-700 text-zinc-100 rounded text-[11px] font-mono text-center outline-none focus:ring-1 focus:ring-blue-500"
                value={layout.gapX !== undefined && !isNaN(layout.gapX) ? formatUnit(layout.gapX, appUnit) : ''}
                onChange={(e) => setLayout(prev => ({ ...prev, gapX: parseUnit(e.target.value, appUnit) }))}
                title={`Horizontal Gap X (${appUnit})`}
              />
              <input 
                type="number" min="0" placeholder="Y"
                className="w-12 min-w-[46px] h-5.5 px-1 bg-zinc-800/90 border border-zinc-700 text-zinc-100 rounded text-[11px] font-mono text-center outline-none focus:ring-1 focus:ring-blue-500"
                value={layout.gapY !== undefined && !isNaN(layout.gapY) ? formatUnit(layout.gapY, appUnit) : ''}
                onChange={(e) => setLayout(prev => ({ ...prev, gapY: parseUnit(e.target.value, appUnit) }))}
                title={`Vertical Gap Y (${appUnit})`}
              />
            </div>

            {/* Margins: Mx and My */}
            <div className="flex items-center gap-1 bg-zinc-950/80 px-1.5 py-0.5 rounded-md border border-zinc-800 shrink-0 h-7" title={`Sheet Outer Margin X & Y in ${appUnit}`}>
              <Maximize className="w-3.5 h-3.5 text-zinc-400 shrink-0" />
              <input 
                type="number" min="0" placeholder="X"
                className="w-12 min-w-[46px] h-5.5 px-1 bg-zinc-800/90 border border-zinc-700 text-zinc-100 rounded text-[11px] font-mono text-center outline-none focus:ring-1 focus:ring-blue-500"
                value={layout.marginX !== undefined && !isNaN(layout.marginX) ? formatUnit(layout.marginX, appUnit) : ''}
                onChange={(e) => setLayout(prev => ({ ...prev, marginX: parseUnit(e.target.value, appUnit) }))}
                title={`Sheet Margin X (${appUnit})`}
              />
              <input 
                type="number" min="0" placeholder="Y"
                className="w-12 min-w-[46px] h-5.5 px-1 bg-zinc-800/90 border border-zinc-700 text-zinc-100 rounded text-[11px] font-mono text-center outline-none focus:ring-1 focus:ring-blue-500"
                value={layout.marginY !== undefined && !isNaN(layout.marginY) ? formatUnit(layout.marginY, appUnit) : ''}
                onChange={(e) => setLayout(prev => ({ ...prev, marginY: parseUnit(e.target.value, appUnit) }))}
                title={`Sheet Margin Y (${appUnit})`}
              />
            </div>

            {/* Quick Auto-Nest & Alignment Buttons */}
            <button 
              onClick={handleAutoCalculate}
              className="p-1.5 bg-blue-950/70 hover:bg-blue-900 text-blue-300 rounded-lg text-xs font-medium border border-blue-800 transition-colors flex items-center cursor-pointer shrink-0"
              title="Auto-Calculate Optimal Grid Layout (Auto-Nest Optimizer)"
            >
              <Zap className="w-3.5 h-3.5 text-blue-400" />
            </button>

            <button 
              onClick={handleCenterOnPage}
              className="p-1.5 bg-zinc-800 hover:bg-zinc-700 text-zinc-300 rounded-lg text-xs font-medium border border-zinc-700 transition-colors cursor-pointer shrink-0"
              title="Center Grid on Sheet (Equalize Margins)"
            >
              <AlignCenter className="w-3.5 h-3.5 text-zinc-300" />
            </button>

            <button 
              onClick={() => setLayout(prev => ({ ...prev, rotateStickers: !prev.rotateStickers }))} 
              className={`p-1.5 rounded-lg text-xs font-medium border transition-colors cursor-pointer shrink-0 ${
                layout.rotateStickers 
                  ? 'bg-blue-600 text-white border-blue-500 shadow-xs' 
                  : 'bg-zinc-800 hover:bg-zinc-700 text-zinc-300 border-zinc-700'
              }`}
              title={layout.rotateStickers ? "Stickers Rotated 90° (Click to reset)" : "Rotate Stickers 90°"}
            >
              <RotateCw className="w-3.5 h-3.5" />
            </button>
          </div>

          <div className="w-px h-4 bg-zinc-800 shrink-0"></div>
          {/* Interaction Modes */}
          <div className="flex items-center gap-0.5 bg-zinc-800/90 p-0.5 rounded-lg border border-zinc-700/80">
            <button 
              onClick={() => setSlotInteractionMode('preview')}
              className={`p-1.5 rounded-md text-xs font-semibold transition-all flex items-center cursor-pointer ${
                slotInteractionMode === 'preview' ? 'bg-zinc-950 text-white shadow-xs border border-zinc-700' : 'text-zinc-400 hover:text-zinc-100'
              }`}
              title="Selection & Inspect Tool"
            >
              <MousePointer className="w-3.5 h-3.5" />
            </button>
            <button 
              onClick={() => setSlotInteractionMode('skip_toggle')}
              className={`p-1.5 rounded-md text-xs font-semibold transition-all flex items-center gap-1 cursor-pointer ${
                slotInteractionMode === 'skip_toggle' ? 'bg-amber-500 text-white shadow-xs' : 'text-zinc-400 hover:text-zinc-100'
              }`}
              title="Skip/Disable Slot Tool (Click any cell on preview sheet to skip it)"
            >
              <Ban className="w-3.5 h-3.5" />
              {disabledSlots.length > 0 && <span className="text-[10px] font-bold">({disabledSlots.length})</span>}
            </button>
            <button 
              onClick={() => setSlotInteractionMode('hand')}
              className={`p-1.5 rounded-md text-xs font-semibold transition-all flex items-center cursor-pointer ${
                slotInteractionMode === 'hand' ? 'bg-zinc-950 text-white shadow-xs border border-zinc-700' : 'text-zinc-400 hover:text-zinc-100'
              }`}
              title="Hand Tool (Hold Spacebar): Drag to pan/move sheet"
            >
              <Hand className="w-3.5 h-3.5" />
            </button>
          </div>

          {disabledSlots.length > 0 && (
            <button
              onClick={clearDisabledSlots}
              className="p-1.5 bg-red-950/40 hover:bg-red-900/60 text-red-400 rounded-lg text-xs font-medium border border-red-900/40 transition-colors cursor-pointer"
              title="Re-enable all skipped cell slots"
            >
              <RotateCcw className="w-3.5 h-3.5" />
            </button>
          )}

          <div className="w-px h-4 bg-zinc-800"></div>

          {/* Prepress Tools, Marks & 3D Proofing */}
          <div id="tour-imposition-prepress-tools" className="flex items-center gap-1.5 shrink-0">
            {/* Visual Debugging Tool: Coordinate Reference & Grid Inspector */}
            <div className="flex items-center gap-0.5 bg-zinc-800/80 p-0.5 rounded-lg border border-zinc-700/60">
              <button
                onClick={() => setShowCoordinateDebugger(prev => !prev)}
                className={`px-2 py-1 rounded text-xs font-semibold transition-all flex items-center gap-1.5 cursor-pointer ${
                  showCoordinateDebugger
                    ? 'bg-cyan-600 text-white border border-cyan-400 shadow-xs ring-1 ring-cyan-400/50'
                    : 'text-zinc-400 hover:text-zinc-200 bg-zinc-800'
                }`}
                title="Toggle Pixel-Perfect Coordinate Debugger & Grid Reference Overlay (Screen Display px vs Print Export pt/mm)"
              >
                <Ruler className="w-3.5 h-3.5 text-cyan-400 animate-pulse" />
                <span className="text-[11px] font-mono font-bold">Coords Grid</span>
              </button>
            </div>

            {/* Safe Zone Toggle & Config */}
            <div className="flex items-center gap-0.5 bg-zinc-800/80 p-0.5 rounded-lg border border-zinc-700/60">
              <button
                onClick={() => setLayout(prev => ({ ...prev, showSafeZone: prev.showSafeZone === false ? true : false }))}
                className={`p-1.5 rounded text-xs font-semibold transition-all flex items-center cursor-pointer ${
                  layout.showSafeZone !== false 
                    ? 'bg-emerald-600/40 text-emerald-200 border border-emerald-500/40 shadow-xs' 
                    : 'text-zinc-400 hover:text-zinc-200'
                }`}
                title="Toggle Safe Zone (Inner Margin) Visual Guide"
              >
                <ShieldCheck className="w-3.5 h-3.5 text-emerald-400" />
              </button>
              <button
                onClick={() => setShowSafeZoneModal(prev => !prev)}
                className={`p-1.5 rounded text-xs transition-colors cursor-pointer ${
                  showSafeZoneModal ? 'bg-emerald-600 text-white' : 'text-zinc-400 hover:text-zinc-100 hover:bg-zinc-700'
                }`}
                title="Safe Zone Settings & Alignment Validator"
              >
                <SlidersHorizontal className="w-3 h-3 text-emerald-300" />
              </button>
            </div>

            {/* Color Bars Toggle & Config */}
            <div className="flex items-center gap-0.5 bg-zinc-800/80 p-0.5 rounded-lg border border-zinc-700/60">
              <button
                onClick={() => setLayout(prev => ({ ...prev, colorBars: !prev.colorBars }))}
                className={`p-1.5 rounded text-xs font-semibold transition-all flex items-center cursor-pointer ${
                  layout.colorBars 
                    ? 'bg-purple-600/40 text-purple-200 border border-purple-500/40 shadow-xs' 
                    : 'text-zinc-400 hover:text-zinc-200'
                }`}
                title="Toggle CMYK & Spot Color Bars"
              >
                <Palette className="w-3.5 h-3.5 text-purple-400" />
              </button>
              <button
                onClick={() => setShowColorBarsModal(prev => !prev)}
                className={`p-1.5 rounded text-xs transition-colors cursor-pointer ${
                  showColorBarsModal ? 'bg-purple-600 text-white' : 'text-zinc-400 hover:text-zinc-100 hover:bg-zinc-700'
                }`}
                title="Color Bars Configuration Panel"
              >
                <SlidersHorizontal className="w-3 h-3 text-purple-300" />
              </button>
            </div>

            {/* Trim & Media Box Toggle & Config */}
            <div className="flex items-center gap-0.5 bg-zinc-800/80 p-0.5 rounded-lg border border-zinc-700/60">
              <button
                onClick={() => setLayout(prev => ({ ...prev, showPdfBoxes: prev.showPdfBoxes === false ? true : false }))}
                className={`p-1.5 rounded text-xs font-semibold transition-all flex items-center cursor-pointer ${
                  layout.showPdfBoxes !== false 
                    ? 'bg-blue-600/40 text-blue-200 border border-blue-500/40 shadow-xs' 
                    : 'text-zinc-400 hover:text-zinc-200'
                }`}
                title="Toggle Trim Box & Media Box Prepress Visual Overlays"
              >
                <Box className="w-3.5 h-3.5 text-blue-400" />
              </button>
              <button
                onClick={() => setShowTrimMediaModal(prev => !prev)}
                className={`p-1.5 rounded text-xs transition-colors cursor-pointer ${
                  showTrimMediaModal ? 'bg-blue-600 text-white' : 'text-zinc-400 hover:text-zinc-100 hover:bg-zinc-700'
                }`}
                title="Trim Box & Media Box Inspector"
              >
                <Scissors className="w-3.5 h-3.5 text-blue-300" />
              </button>
            </div>

            {/* Plotter Marks & CutContour Toggle & Config */}
            <div className="flex items-center gap-0.5 bg-zinc-800/80 p-0.5 rounded-lg border border-zinc-700/60">
              <button
                onClick={() => setLayout(prev => ({ ...prev, plotterMarks: !prev.plotterMarks, enableCutContour: !prev.enableCutContour }))}
                className={`p-1.5 rounded text-xs font-semibold transition-all flex items-center cursor-pointer ${
                  layout.plotterMarks || layout.enableCutContour
                    ? 'bg-pink-600/40 text-pink-200 border border-pink-500/40 shadow-xs' 
                    : 'text-zinc-400 hover:text-zinc-200'
                }`}
                title="Toggle Cutting Plotter Marks & CutContour Spot Layer"
              >
                <Target className="w-3.5 h-3.5 text-pink-400" />
              </button>
              <button
                onClick={() => setShowPlotterModal(prev => !prev)}
                className={`p-1.5 rounded text-xs transition-colors cursor-pointer ${
                  showPlotterModal ? 'bg-pink-600 text-white' : 'text-zinc-400 hover:text-zinc-100 hover:bg-zinc-700'
                }`}
                title="Cutting Plotter & CutContour Finishing Inspector"
              >
                <SlidersHorizontal className="w-3 h-3 text-pink-300" />
              </button>
            </div>

            <div className="w-px h-4 bg-zinc-800"></div>

            {/* Studio Quick Launch Buttons */}
            <button
              onClick={() => setShowGangingModal(prev => !prev)}
              className={`p-1.5 rounded-lg text-xs font-semibold transition-all flex items-center cursor-pointer ${
                layout.enableGanging || layout.impositionType !== 'single_sided'
                  ? 'bg-purple-600 text-white shadow-xs' 
                  : 'text-zinc-400 hover:text-zinc-200 hover:bg-zinc-800'
              }`}
              title="Multi-Job Ganging & Work-and-Turn Studio"
            >
              <Frame className="w-3.5 h-3.5 text-purple-300" />
            </button>

            <button
              onClick={() => setShow3DProofModal(true)}
              className="p-1.5 rounded-lg text-xs font-bold transition-all flex items-center cursor-pointer bg-indigo-600 hover:bg-indigo-500 text-white shadow-xs"
              title="Open 3D Packaging & Label Mockup Studio (Synced State)"
            >
              <CubeIcon className="w-3.5 h-3.5 text-indigo-100" />
            </button>

            <button
              onClick={() => setShowLayoutOverlay(prev => !prev)}
              className={`p-1.5 rounded-lg text-xs font-semibold transition-all flex items-center cursor-pointer ${
                showLayoutOverlay
                  ? 'bg-blue-600 text-white shadow-xs border border-blue-400/50'
                  : 'text-zinc-400 hover:text-zinc-200 hover:bg-zinc-800'
              }`}
              title="Toggle Sheet Layout & Dimensions Legend Overlay"
            >
              <Info className="w-3.5 h-3.5 text-blue-200" />
            </button>

            {/* Canvas Dotted Grid Background Mode Selector */}
            <div className="flex items-center gap-0.5 bg-zinc-800/80 p-0.5 rounded-lg border border-zinc-700/60" title="Canvas Background: Dotted Grid (Reference Image) / Dark Dots / Solid">
              <button
                onClick={() => {
                  const nextMode = canvasBgMode === 'dotted_light' ? 'dotted_dark' : (canvasBgMode === 'dotted_dark' ? 'solid_dark' : 'dotted_light');
                  handleToggleCanvasBg(nextMode);
                }}
                className={`p-1.5 rounded text-xs font-semibold transition-all flex items-center gap-1 cursor-pointer ${
                  canvasBgMode === 'dotted_light'
                    ? 'bg-blue-600 text-white shadow-xs border border-blue-400/40'
                    : (canvasBgMode === 'dotted_dark' ? 'bg-zinc-700 text-blue-300 border border-zinc-600' : 'text-zinc-400 hover:text-zinc-200')
                }`}
                title={`Canvas Background Grid: ${
                  canvasBgMode === 'dotted_light' 
                    ? 'Light Dotted Grid (Reference Image)' 
                    : (canvasBgMode === 'dotted_dark' ? 'Dark Dotted Grid' : 'Solid Dark')
                } (Click to toggle)`}
              >
                <CircleDot className="w-3.5 h-3.5 text-blue-200" />
                <span className="text-[10px] font-mono hidden md:inline">
                  {canvasBgMode === 'dotted_light' ? 'Dot Grid' : (canvasBgMode === 'dotted_dark' ? 'Dark Dots' : 'Solid')}
                </span>
              </button>
            </div>
          </div>
        </div>

        {/* Zoom & Sheet Navigation Bar */}
        <div className="flex items-center gap-1.5 shrink-0">
          {/* Zoom Controls */}
          <div className="flex items-center gap-0.5 bg-zinc-800/90 p-0.5 rounded-lg border border-zinc-700/80">
            <button 
              onClick={() => setZoomLevel(z => Math.max(0.15, Number((z - 0.25).toFixed(2))))}
              className="p-1 hover:bg-zinc-700 rounded text-zinc-300 transition-colors cursor-pointer"
              title="Zoom Out (-25%)"
            >
              <ZoomOut className="w-3.5 h-3.5" />
            </button>
            <select
              value={Math.round(zoomLevel * 100)}
              onChange={e => setZoomLevel(Number(e.target.value) / 100)}
              className="bg-zinc-950 text-blue-400 font-mono text-[11px] font-bold rounded px-1 py-0.5 border border-zinc-700 outline-none cursor-pointer"
              title="Select Zoom Level (Up to 500%)"
            >
              <option value="25">25%</option>
              <option value="50">50%</option>
              <option value="75">75%</option>
              <option value="100">100%</option>
              <option value="150">150%</option>
              <option value="200">200%</option>
              <option value="300">300%</option>
              <option value="400">400%</option>
              <option value="500">500%</option>
            </select>
            <button 
              onClick={() => setZoomLevel(z => Math.min(5, Number((z + 0.25).toFixed(2))))}
              className="p-1 hover:bg-zinc-700 rounded text-zinc-300 transition-colors cursor-pointer"
              title="Zoom In (+25%)"
            >
              <ZoomIn className="w-3.5 h-3.5" />
            </button>
            <button 
              onClick={handleFitToPage}
              className="p-1 bg-blue-900/40 hover:bg-blue-800/60 text-blue-300 border border-blue-700/50 rounded text-xs font-semibold flex items-center transition-all cursor-pointer"
              title="Fit to Page Width & Height"
            >
              <Maximize2 className="w-3.5 h-3.5" />
            </button>
          </div>

          <div className="w-px h-4 bg-zinc-800"></div>

          {/* Sheet Navigation Controls */}
          <div className="flex items-center gap-0.5 bg-zinc-800/90 p-0.5 rounded-lg border border-zinc-700/80">
            <button 
              onClick={() => setPreviewPage(1)}
              disabled={previewPage <= 1}
              className="p-1 hover:bg-zinc-700 rounded text-zinc-300 transition-colors disabled:opacity-30 disabled:hover:bg-transparent cursor-pointer"
              title="First Sheet"
            >
              <ChevronsLeft className="w-3.5 h-3.5" />
            </button>
            <button 
              onClick={() => setPreviewPage(p => Math.max(1, p - 1))}
              disabled={previewPage <= 1}
              className="p-1 hover:bg-zinc-700 rounded text-zinc-300 transition-colors disabled:opacity-30 disabled:hover:bg-transparent cursor-pointer"
              title="Previous Sheet"
            >
              <ChevronLeft className="w-3.5 h-3.5" />
            </button>

            <div className="flex items-center gap-1 px-1">
              <span className="text-[11px] font-mono text-zinc-400 font-medium">Sheet</span>
              <select
                value={previewPage}
                onChange={e => setPreviewPage(Number(e.target.value))}
                className="bg-zinc-950 text-emerald-400 font-mono text-xs font-bold rounded px-1.5 py-0.5 border border-zinc-700 outline-none cursor-pointer"
                title="Select Sheet Page"
              >
                {/* Optimized option list capped for high-volume sheets */}
                {pagesNeeded <= 200 ? (
                  Array.from({ length: pagesNeeded }, (_, i) => (
                    <option key={i + 1} value={i + 1}>
                      {i + 1}
                    </option>
                  ))
                ) : (
                  <>
                    {/* Render first 50 pages */}
                    {Array.from({ length: 50 }, (_, i) => (
                      <option key={i + 1} value={i + 1}>
                        {i + 1}
                      </option>
                    ))}
                    {/* Render around current page if outside initial range */}
                    {previewPage > 50 && previewPage < pagesNeeded - 50 && (
                      <option key={previewPage} value={previewPage}>
                        {previewPage}
                      </option>
                    )}
                    {/* Render last 50 pages */}
                    {Array.from({ length: 50 }, (_, i) => {
                      const pNum = pagesNeeded - 49 + i;
                      return (
                        <option key={pNum} value={pNum}>
                          {pNum}
                        </option>
                      );
                    })}
                  </>
                )}
              </select>
              <span className="text-[11px] font-mono text-zinc-400 font-medium">of {pagesNeeded}</span>
            </div>

            <button 
              onClick={() => setPreviewPage(p => Math.min(pagesNeeded, p + 1))}
              disabled={previewPage >= pagesNeeded}
              className="p-1 hover:bg-zinc-700 rounded text-zinc-300 transition-colors disabled:opacity-30 disabled:hover:bg-transparent cursor-pointer"
              title="Next Sheet"
            >
              <ChevronRight className="w-3.5 h-3.5" />
            </button>
            <button 
              onClick={() => setPreviewPage(pagesNeeded)}
              disabled={previewPage >= pagesNeeded}
              className="p-1 hover:bg-zinc-700 rounded text-zinc-300 transition-colors disabled:opacity-30 disabled:hover:bg-transparent cursor-pointer"
              title="Last Sheet"
            >
              <ChevronsRight className="w-3.5 h-3.5" />
            </button>
          </div>
        </div>
      </div>

      {/* Ganging Overflow Banner in Preview if active */}
      {gangingError && (
        <div className="bg-red-950/95 border-b border-red-500/70 px-4 py-2 flex items-center justify-between text-xs text-red-200 z-10">
          <div className="flex items-center gap-2">
            <AlertTriangle className="w-4 h-4 text-red-400 shrink-0" />
            <span><strong className="font-semibold text-red-100">Ganging Error:</strong> {gangingError}</span>
          </div>
          <button
            onClick={() => setShowGangingModal(true)}
            className="px-2.5 py-1 bg-red-900/80 hover:bg-red-800 text-red-100 rounded text-[11px] font-semibold border border-red-700/50 cursor-pointer"
          >
            Fix Ganging Grid
          </button>
        </div>
      )}

            {/* Visual Debugging Floating HUD Control Panel */}
      {showCoordinateDebugger && (
        <div className="absolute top-12 left-1/2 -translate-x-1/2 z-40 bg-zinc-900/95 text-zinc-100 px-3 py-1.5 rounded-lg border border-cyan-500/60 shadow-xl flex items-center gap-3 backdrop-blur-md text-xs font-mono">
          <div className="flex items-center gap-1.5 text-cyan-400 font-bold">
            <Ruler className="w-4 h-4 text-cyan-400 animate-pulse" />
            <span>Coordinate Debugger</span>
          </div>

          <div className="w-px h-4 bg-zinc-800" />

          {/* Mode Switcher */}
          <div className="flex items-center gap-1 bg-zinc-950 p-0.5 rounded border border-zinc-800">
            <button
              onClick={() => setCoordinateMode('screen')}
              className={`px-2 py-0.5 rounded text-[10.5px] font-bold transition-all cursor-pointer ${
                coordinateMode === 'screen' ? 'bg-cyan-600 text-white shadow' : 'text-zinc-400 hover:text-zinc-200'
              }`}
            >
              Screen Display (px)
            </button>
            <button
              onClick={() => setCoordinateMode('print_pt')}
              className={`px-2 py-0.5 rounded text-[10.5px] font-bold transition-all cursor-pointer ${
                coordinateMode === 'print_pt' ? 'bg-amber-600 text-white shadow' : 'text-zinc-400 hover:text-zinc-200'
              }`}
            >
              Print Export (pt)
            </button>
            <button
              onClick={() => setCoordinateMode('print_mm')}
              className={`px-2 py-0.5 rounded text-[10.5px] font-bold transition-all cursor-pointer ${
                coordinateMode === 'print_mm' ? 'bg-emerald-600 text-white shadow' : 'text-zinc-400 hover:text-zinc-200'
              }`}
            >
              Print Metric (mm)
            </button>
          </div>

          <div className="w-px h-4 bg-zinc-800" />

          {/* Grid Step Selector */}
          <div className="flex items-center gap-1 text-[11px]">
            <span className="text-zinc-400">Step:</span>
            <select
              value={debugGridStepMm}
              onChange={(e) => setDebugGridStepMm(Number(e.target.value))}
              className="bg-zinc-800 text-zinc-200 text-[10.5px] font-mono px-1.5 py-0.5 rounded border border-zinc-700 outline-none"
            >
              <option value={5}>5 mm (14.2 pt)</option>
              <option value={10}>10 mm (28.3 pt)</option>
              <option value={20}>20 mm (56.7 pt)</option>
              <option value={50}>50 mm (141.7 pt)</option>
            </select>
          </div>

          <div className="w-px h-4 bg-zinc-800" />

          {/* Discrepancy Parity Indicator */}
          <div className="flex items-center gap-1 text-[10px] text-emerald-400 bg-emerald-950/80 px-2 py-0.5 rounded border border-emerald-500/40">
            <ShieldCheck className="w-3.5 h-3.5 text-emerald-400" />
            <span>0.75 Ratio (96 DPI ➔ 72 DPI) • 100% Parity Aligned</span>
          </div>

          <button
            onClick={() => setShowCoordinateDebugger(false)}
            className="text-zinc-400 hover:text-white ml-1 text-xs cursor-pointer"
            title="Close Debugger"
          >
            ✕
          </button>
        </div>
      )}

      <div 
        ref={containerRef} 
        onMouseDown={handleMouseDown}
        onMouseMove={handleMouseMove}
        onMouseUp={handleMouseUp}
        onMouseLeave={handleMouseUp}
        onTouchStart={handleTouchStart}
        onTouchMove={handleTouchMove}
        onTouchEnd={handleMouseUp}
        onWheel={handleWheel}
        className={`flex-1 min-h-0 w-full overflow-hidden relative select-none transition-colors duration-200 ${
          slotInteractionMode === 'hand' 
            ? (isDragging ? 'cursor-grabbing' : 'cursor-grab') 
            : 'cursor-default'
        }`}
        style={canvasBgStyle}
      >
        <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
          <div 
            className={`bg-white relative shrink-0 pointer-events-auto ${isDragging ? '' : 'transition-all duration-200 ease-in-out'}`}
            style={{ 
              width: `${sheetWidth}px`,
              height: `${sheetHeight}px`,
              transform: `translate(${panOffset.x}px, ${panOffset.y}px)`,
              boxShadow: canvasBgMode === 'dotted_light'
                ? '0 25px 60px -15px rgba(15, 23, 42, 0.28), 0 10px 25px -6px rgba(15, 23, 42, 0.16), 0 0 0 1px rgba(15, 23, 42, 0.08)'
                : '0 25px 50px -12px rgba(0, 0, 0, 0.7), 0 0 0 1px rgba(255, 255, 255, 0.12)',
            }}
          >
          
          {/* Visual Debugging Tool: Pixel-Perfect Grid Coordinate Overlay */}
          {showCoordinateDebugger && (() => {
            const stepPx = debugGridStepMm * 3.779527559 * previewScale;
            const gridColsCount = Math.floor(sheetWidth / Math.max(1, stepPx));
            const gridRowsCount = Math.floor(sheetHeight / Math.max(1, stepPx));

            return (
              <div 
                className="absolute inset-0 pointer-events-none z-50 overflow-visible"
                onMouseMove={(e) => {
                  const rect = e.currentTarget.getBoundingClientRect();
                  const pxX = (e.clientX - rect.left) / previewScale;
                  const pxY = (e.clientY - rect.top) / previewScale;
                  setDebugHoverCoords({ x: pxX, y: pxY });
                }}
                onMouseLeave={() => setDebugHoverCoords(null)}
              >
                {/* SVG Precision Coordinate Grid Lines */}
                <svg className="absolute inset-0 w-full h-full overflow-visible pointer-events-none">
                  <defs>
                    <pattern id="coord-debug-grid" width={stepPx} height={stepPx} patternUnits="userSpaceOnUse">
                      <path d={`M ${stepPx} 0 L 0 0 0 ${stepPx}`} fill="none" stroke="rgba(6, 182, 212, 0.35)" strokeWidth="1" strokeDasharray="2,2" />
                    </pattern>
                  </defs>
                  <rect width="100%" height="100%" fill="url(#coord-debug-grid)" />

                  {/* Major Origin Axes */}
                  <line x1="0" y1="0" x2={sheetWidth} y2="0" stroke="#06b6d4" strokeWidth="2" />
                  <line x1="0" y1="0" x2="0" y2={sheetHeight} stroke="#06b6d4" strokeWidth="2" />
                  <line x1="0" y1={sheetHeight} x2={sheetWidth} y2={sheetHeight} stroke="#f59e0b" strokeWidth="2" />

                  {/* Grid Tick Labels */}
                  {Array.from({ length: gridColsCount + 1 }).map((_, colIdx) => {
                    const xPx = colIdx * stepPx;
                    const mmVal = (colIdx * debugGridStepMm).toFixed(0);
                    const ptVal = (colIdx * debugGridStepMm * 2.83465).toFixed(0);
                    const pxVal = (xPx / previewScale).toFixed(0);
                    const labelText = coordinateMode === 'screen' ? `${pxVal}px` : coordinateMode === 'print_pt' ? `${ptVal}pt` : `${mmVal}mm`;

                    return (
                      <g key={`col-tick-${colIdx}`}>
                        <line x1={xPx} y1="0" x2={xPx} y2="6" stroke="#06b6d4" strokeWidth="1.5" />
                        <line x1={xPx} y1={sheetHeight - 6} x2={xPx} y2={sheetHeight} stroke="#f59e0b" strokeWidth="1.5" />
                        <text x={xPx + 2} y="12" fill="#0891b2" fontSize="9" fontFamily="monospace" fontWeight="bold">
                          {labelText}
                        </text>
                      </g>
                    );
                  })}

                  {Array.from({ length: gridRowsCount + 1 }).map((_, rowIdx) => {
                    const yPx = rowIdx * stepPx;
                    const mmValBottom = ((sheetHeight / previewScale / 3.779527559) - (rowIdx * debugGridStepMm)).toFixed(0);
                    const ptValBottom = ((sheetHeight / previewScale * 0.75) - (rowIdx * debugGridStepMm * 2.83465)).toFixed(0);
                    const pxValTop = (yPx / previewScale).toFixed(0);
                    
                    const labelText = coordinateMode === 'screen' 
                      ? `${pxValTop}px`
                      : coordinateMode === 'print_pt' 
                      ? `${ptValBottom}pt`
                      : `${mmValBottom}mm`;

                    return (
                      <g key={`row-tick-${rowIdx}`}>
                        <line x1="0" y1={yPx} x2="6" y2={yPx} stroke="#06b6d4" strokeWidth="1.5" />
                        <text x="8" y={yPx + 3} fill="#0891b2" fontSize="9" fontFamily="monospace" fontWeight="bold">
                          {labelText}
                        </text>
                      </g>
                    );
                  })}
                </svg>

                {/* Origin (0,0) Target Indicator */}
                <div 
                  className={`absolute p-1 flex items-center gap-1.5 rounded border shadow-lg ${
                    coordinateMode === 'screen' 
                      ? 'top-0 left-0 bg-cyan-950/95 text-cyan-200 border-cyan-400' 
                      : 'bottom-0 left-0 bg-amber-950/95 text-amber-200 border-amber-400'
                  }`}
                  style={{ zIndex: 60 }}
                >
                  <div className="w-2.5 h-2.5 rounded-full bg-cyan-400 animate-ping shrink-0" />
                  <span className="text-[10px] font-mono font-bold">
                    {coordinateMode === 'screen' ? 'SCREEN ORIGIN (0,0 px) [Top-Left]' : 'PRINT EXPORT ORIGIN (0,0 pt) [Bottom-Left]'}
                  </span>
                </div>

                {/* Mouse Hover Live Laser Crosshairs Tracking Badge */}
                {debugHoverCoords && (
                  <div 
                    className="absolute pointer-events-none flex flex-col gap-1 bg-zinc-950/95 text-zinc-100 p-2 rounded-md border border-cyan-400/80 shadow-2xl backdrop-blur-md"
                    style={{ 
                      left: `${debugHoverCoords.x * previewScale + 12}px`, 
                      top: `${debugHoverCoords.y * previewScale + 12}px`, 
                      zIndex: 100 
                    }}
                  >
                    <div className="flex items-center gap-2 border-b border-zinc-800 pb-1 text-[10px] font-mono text-cyan-300 font-bold">
                      <Target className="w-3.5 h-3.5 text-cyan-400 animate-spin" />
                      <span>LIVE COORDINATE INSPECTOR</span>
                    </div>
                    <div className="grid grid-cols-2 gap-x-3 gap-y-0.5 text-[9.5px] font-mono">
                      <span className="text-zinc-400">Screen Display:</span>
                      <span className="text-cyan-300 font-bold">X: {debugHoverCoords.x.toFixed(1)}px, Y: {debugHoverCoords.y.toFixed(1)}px</span>
                      
                      <span className="text-zinc-400">Print Export PDF:</span>
                      <span className="text-amber-300 font-bold">X: {(debugHoverCoords.x * 0.75).toFixed(1)}pt, Y: {((finalSheetHeight - debugHoverCoords.y) * 0.75).toFixed(1)}pt</span>
                      
                      <span className="text-zinc-400">Print Metric:</span>
                      <span className="text-emerald-300 font-bold">X: {(debugHoverCoords.x / 3.779527559).toFixed(1)}mm, Y: {((finalSheetHeight - debugHoverCoords.y) / 3.779527559).toFixed(1)}mm</span>
                    </div>
                  </div>
                )}
              </div>
            );
          })()}

          {/* Visual representation of the layout inside the sheet */}
          <div className="absolute inset-0 border border-red-200" style={{ 
            left: `${marginX}px`,
            top: `${marginY}px`,
            right: `${marginX}px`,
            bottom: `${marginY}px`
          }}>
            <span className="absolute -top-4 left-0 text-[10px] text-red-400 font-medium whitespace-nowrap">Page Margin / Safe Zone</span>

            {/* Plotter Registration Marks (ARMS / OPOS / Graphtec / Roland) */}
            {layout.plotterMarks && (() => {
              const pType = layout.plotterMarkType || 'arms';
              const pOffset = (layout.plotterMarkOffset || 15) * previewScale;
              const pSize = (layout.plotterMarkSize || 12) * previewScale;

              return (
                <div className="absolute inset-0 pointer-events-none" style={{ zIndex: 25 }}>
                  {/* Corner positions */}
                  {[
                    { style: { left: `${pOffset}px`, top: `${pOffset}px` }, label: 'TL' },
                    { style: { right: `${pOffset}px`, top: `${pOffset}px` }, label: 'TR' },
                    { style: { left: `${pOffset}px`, bottom: `${pOffset}px` }, label: 'BL' },
                    { style: { right: `${pOffset}px`, bottom: `${pOffset}px` }, label: 'BR' },
                  ].map((pos, idx) => (
                    <div key={idx} className="absolute flex items-center justify-center" style={{ ...pos.style, width: `${pSize}px`, height: `${pSize}px`, transform: 'translate(-50%, -50%)' }}>
                      {pType === 'opos' ? (
                        <div className="w-full h-full border-2 border-black bg-black/10 rounded-xs flex items-center justify-center">
                          <div className="w-1 h-1 bg-black rounded-full"></div>
                        </div>
                      ) : pType === 'roland' ? (
                        <div className="w-full h-full rounded-full border-2 border-black flex items-center justify-center relative">
                          <div className="w-full h-0.5 bg-black absolute"></div>
                          <div className="h-full w-0.5 bg-black absolute"></div>
                          <div className="w-2 h-2 rounded-full bg-black"></div>
                        </div>
                      ) : pType === 'barcode_target' ? (
                        <div className="flex flex-col items-center justify-center">
                          <div className="w-3 h-3 rounded-full bg-black border border-white shadow-xs"></div>
                          {idx === 2 && (
                            <div className="absolute top-4 left-0 bg-black text-white text-[8px] font-mono p-1 rounded border border-zinc-700 flex items-center gap-1 shadow-md">
                              <QrCode className="w-3 h-3 text-pink-400" />
                              <span>JOB-CUT-9823</span>
                            </div>
                          )}
                        </div>
                      ) : (
                        /* ARMS / Graphtec Circle Target */
                        <div className="w-full h-full rounded-full bg-black flex items-center justify-center relative border border-white shadow-xs">
                          <div className="w-1.5 h-1.5 rounded-full bg-white"></div>
                        </div>
                      )}
                    </div>
                  ))}
                  
                  <div className="absolute top-1 left-1/2 -translate-x-1/2 bg-pink-950/90 text-pink-300 border border-pink-500/40 text-[9px] font-mono px-2 py-0.5 rounded shadow">
                    Plotter Marks: {pType.toUpperCase()} (Safe Margin: {(layout.plotterMarkOffset || 15)}pt)
                  </div>
                </div>
              );
            })()}

            {/* Registration Marks (Crosshairs) */}
            {layout.registrationMarks && (
              <>
                {/* Top Left */}
                <div className="absolute flex items-center justify-center border border-black rounded-full" style={{ left: '-12px', top: '-12px', width: '8px', height: '8px', zIndex: 10 }}>
                   <div className="w-full h-px bg-black absolute"></div>
                   <div className="h-full w-px bg-black absolute"></div>
                </div>
                {/* Top Right */}
                <div className="absolute flex items-center justify-center border border-black rounded-full" style={{ right: '-12px', top: '-12px', width: '8px', height: '8px', zIndex: 10 }}>
                   <div className="w-full h-px bg-black absolute"></div>
                   <div className="h-full w-px bg-black absolute"></div>
                </div>
                {/* Bottom Left */}
                <div className="absolute flex items-center justify-center border border-black rounded-full" style={{ left: '-12px', bottom: '-12px', width: '8px', height: '8px', zIndex: 10 }}>
                   <div className="w-full h-px bg-black absolute"></div>
                   <div className="h-full w-px bg-black absolute"></div>
                </div>
                {/* Bottom Right */}
                <div className="absolute flex items-center justify-center border border-black rounded-full" style={{ right: '-12px', bottom: '-12px', width: '8px', height: '8px', zIndex: 10 }}>
                   <div className="w-full h-px bg-black absolute"></div>
                   <div className="h-full w-px bg-black absolute"></div>
                </div>
                {/* Top Center */}
                <div className="absolute flex items-center justify-center border border-black rounded-full" style={{ left: '50%', transform: 'translateX(-50%)', top: '-12px', width: '8px', height: '8px', zIndex: 10 }}>
                   <div className="w-full h-px bg-black absolute"></div>
                   <div className="h-full w-px bg-black absolute"></div>
                </div>
                {/* Bottom Center */}
                <div className="absolute flex items-center justify-center border border-black rounded-full" style={{ left: '50%', transform: 'translateX(-50%)', bottom: '-12px', width: '8px', height: '8px', zIndex: 10 }}>
                   <div className="w-full h-px bg-black absolute"></div>
                   <div className="h-full w-px bg-black absolute"></div>
                </div>
                {/* Left Center */}
                <div className="absolute flex items-center justify-center border border-black rounded-full" style={{ top: '50%', transform: 'translateY(-50%)', left: '-12px', width: '8px', height: '8px', zIndex: 10 }}>
                   <div className="w-full h-px bg-black absolute"></div>
                   <div className="h-full w-px bg-black absolute"></div>
                </div>
                {/* Right Center */}
                <div className="absolute flex items-center justify-center border border-black rounded-full" style={{ top: '50%', transform: 'translateY(-50%)', right: '-12px', width: '8px', height: '8px', zIndex: 10 }}>
                   <div className="w-full h-px bg-black absolute"></div>
                   <div className="h-full w-px bg-black absolute"></div>
                </div>
              </>
            )}


            {/* Continuous Roll Feed Overlay Banner (Phase 4) */}
            {layout.isRollMode && (
              <div className="absolute -top-6 left-0 right-0 bg-amber-900/90 text-white text-[9px] font-mono font-bold px-2 py-0.5 flex items-center justify-between z-30 shadow-md border border-amber-500 rounded-t-md">
                <span className="flex items-center gap-1.5">
                  <span className="w-2 h-2 rounded-full bg-amber-400 animate-pulse" />
                  CONTINUOUS ROLL WEB • {layout.rollWidthMm || 108}mm WEB • {layout.trackCount || 1}-TRACK LANES
                </span>
                <span className="bg-amber-950 px-1.5 py-0.5 rounded text-amber-300 border border-amber-700">
                  UNWIND DIR #{layout.unwindDirection || 1}
                </span>
              </div>
            )}

            {useMemo(() => Array.from({ length: rows }).map((_, r) => (
              Array.from({ length: cols }).map((_, c) => (
                <ImpositionSlot
                  key={`${r}-${c}`}
                  r={r} c={c} rows={rows} cols={cols} stepX={stepX} stepY={stepY} bleedVal={bleedVal}
                  stickerWidth={stickerWidth} stickerHeight={stickerHeight} previewScale={previewScale}
                  stickerVisualRotation={stickerVisualRotation} viewSide={viewSide}
                  impositionType={impositionType} isGangingActive={isGangingActive}
                  gangingSlotMap={gangingSlotMap} slotInteractionMode={slotInteractionMode}
                  disabledSlots={disabledSlots} toggleSlotDisabled={toggleSlotDisabled}
                  getPreviewRecordIndex={getPreviewRecordIndex} layout={layout} template={template}
                  pagesNeeded={pagesNeeded} previewPage={previewPage} stickersPerPage={stickersPerPage}
                  totalStickers={totalStickers} copies={copies} csvData={csvData}
                  effectiveLen={effectiveLen} effectiveOffset={effectiveOffset}
                />
              ))
            )), [rows, cols, disabledSlots, slotInteractionMode, impositionType, isGangingActive, gangingSlotMap, stepX, stepY, bleedVal, stickerWidth, stickerHeight, stickerVisualRotation, viewSide, layout, template, getPreviewRecordIndex, pagesNeeded, previewPage, stickersPerPage, totalStickers, copies, csvData, effectiveLen, effectiveOffset, toggleSlotDisabled])}
            
            {layout.showTrimMarks && ['outer_grid', 'all_marks'].includes(layout.trimMarkType || 'outer_grid') && (
              <>
                {/* Vertical outer cutting ticks */}
                {cutXs.map((x) => (
                  <React.Fragment key={`v-outer-${x}`}>
                    {/* Top tick - pointing outwards (up) */}
                    <div 
                      className="absolute w-px bg-black" 
                      style={{ 
                        left: `${x}px`, 
                        top: `${minY - effectiveOffset - effectiveLen}px`, 
                        height: `${effectiveLen}px`,
                        zIndex: 10 
                      }}
                    ></div>
                    {/* Bottom tick - pointing outwards (down) */}
                    <div 
                      className="absolute w-px bg-black" 
                      style={{ 
                        left: `${x}px`, 
                        top: `${maxY + effectiveOffset}px`, 
                        height: `${effectiveLen}px`,
                        zIndex: 10 
                      }}
                    ></div>
                  </React.Fragment>
                ))}
                
                {/* Horizontal outer cutting ticks */}
                {cutYs.map((y) => (
                  <React.Fragment key={`h-outer-${y}`}>
                    {/* Left tick - pointing outwards (left) */}
                    <div 
                      className="absolute h-px bg-black" 
                      style={{ 
                        top: `${y}px`, 
                        left: `${minX - effectiveOffset - effectiveLen}px`, 
                        width: `${effectiveLen}px`,
                        zIndex: 10 
                      }}
                    ></div>
                    {/* Right tick - pointing outwards (right) */}
                    <div 
                      className="absolute h-px bg-black" 
                      style={{ 
                        top: `${y}px`, 
                        left: `${maxX + effectiveOffset}px`, 
                        width: `${effectiveLen}px`,
                        zIndex: 10 
                      }}
                    ></div>
                  </React.Fragment>
                ))}
              </>
            )}

            {layout.showTrimMarks && ['corner_marks', 'all_marks'].includes(layout.trimMarkType || '') && (
              <>
                 {/* Top Left Corner */}
                 <div className="absolute h-px bg-black" style={{ width: `${effectiveLen}px`, left: `${minX - effectiveOffset - effectiveLen}px`, top: `${minY}px`, zIndex: 10 }}></div>
                 <div className="absolute w-px bg-black" style={{ height: `${effectiveLen}px`, left: `${minX}px`, top: `${minY - effectiveOffset - effectiveLen}px`, zIndex: 10 }}></div>
                 
                 {/* Top Right Corner */}
                 <div className="absolute h-px bg-black" style={{ width: `${effectiveLen}px`, left: `${maxX + effectiveOffset}px`, top: `${minY}px`, zIndex: 10 }}></div>
                 <div className="absolute w-px bg-black" style={{ height: `${effectiveLen}px`, left: `${maxX}px`, top: `${minY - effectiveOffset - effectiveLen}px`, zIndex: 10 }}></div>

                 {/* Bottom Left Corner */}
                 <div className="absolute h-px bg-black" style={{ width: `${effectiveLen}px`, left: `${minX - effectiveOffset - effectiveLen}px`, top: `${maxY}px`, zIndex: 10 }}></div>
                 <div className="absolute w-px bg-black" style={{ height: `${effectiveLen}px`, left: `${minX}px`, top: `${maxY + effectiveOffset}px`, zIndex: 10 }}></div>
                 
                 {/* Bottom Right Corner */}
                 <div className="absolute h-px bg-black" style={{ width: `${effectiveLen}px`, left: `${maxX + effectiveOffset}px`, top: `${maxY}px`, zIndex: 10 }}></div>
                 <div className="absolute w-px bg-black" style={{ height: `${effectiveLen}px`, left: `${maxX}px`, top: `${maxY + effectiveOffset}px`, zIndex: 10 }}></div>
              </>
            )}
          </div>

          {/* Sheet Information Slug (Job Info) */}
          {layout.printSlug && (() => {
            const pos = layout.printSlugPosition || 'bottom_left';
            const offset = Math.max((layout.printSlugOffset !== undefined ? layout.printSlugOffset : 8.5) * previewScale, 2);
            const sideMargin = Math.max(marginX, 8 * previewScale);
            const isTop = pos.startsWith('top');
            const isCenter = pos.endsWith('center');
            const isRight = pos.endsWith('right');

            const style: React.CSSProperties = {
              position: 'absolute',
              fontSize: `${Math.max(6.5 * previewScale, 7.5)}px`,
              zIndex: 25,
              whiteSpace: 'nowrap',
              color: '#3f3f46',
              fontFamily: 'monospace',
              pointerEvents: 'none',
              ...(isTop ? { top: `${offset}px` } : { bottom: `${offset}px` }),
              ...(isCenter 
                ? { left: '50%', transform: 'translateX(-50%)' }
                : isRight 
                  ? { right: `${sideMargin}px` }
                  : { left: `${sideMargin}px` }
              )
            };

            const totalPages = Math.max(1, Math.ceil(totalStickers / (rows * cols || 1)));
            const sampleRecord = csvData?.rows?.[(previewPage - 1) * (rows * cols)] || csvData?.rows?.[0];
            const slugContent = formatPrintSlug(layout, sampleRecord, {
              sheetIndex: previewPage,
              totalSheets: totalPages,
              totalRecords: totalStickers,
              pageSize: layout.pageSize,
              templateWidthPt: template?.width,
              templateHeightPt: template?.height
            });

            return (
              <div style={style}>
                {slugContent}
              </div>
            );
          })()}

          {/* CMYK & Custom Color Bars */}
          {layout.colorBars && (() => {
            const pos = layout.colorBarsPosition || 'top_center';
            const offset = Math.max((layout.colorBarsOffset !== undefined ? layout.colorBarsOffset : 8.5) * previewScale, 2);
            const sideMargin = Math.max(marginX, 10 * previewScale);
            const isVertical = pos === 'left' || pos === 'right';
            const isTop = pos.startsWith('top');
            const isCenter = pos.includes('center');
            const isRight = pos.includes('right');
            const showNames = layout.colorBarsShowNames ?? false;

            let posStyle: React.CSSProperties = {};
            if (pos === 'left') {
              posStyle = { left: `${offset}px`, top: '50%', transform: 'translateY(-50%)' };
            } else if (pos === 'right') {
              posStyle = { right: `${offset}px`, top: '50%', transform: 'translateY(-50%)' };
            } else {
              posStyle = {
                ...(isTop ? { top: `${offset}px` } : { bottom: `${offset}px` }),
                ...(isCenter 
                  ? { left: '50%', transform: 'translateX(-50%)' } 
                  : isRight 
                    ? { right: `${sideMargin}px` } 
                    : { left: `${sideMargin}px` }
                )
              };
            }

            const patches = getActiveColorBarPatches(layout);
            const userSize = layout.colorBarsPatchSize || 8;
            const patchW = isVertical ? Math.max(userSize * 0.7 * previewScale, 5) : Math.max(userSize * 1.2 * previewScale, 7);
            const patchH = isVertical ? Math.max(userSize * 1.2 * previewScale, 7) : Math.max(userSize * 0.7 * previewScale, 5);

            return (
              <div 
                className="absolute flex shadow-xs pointer-events-none items-center" 
                style={{
                  flexDirection: isVertical ? 'column' : 'row',
                  gap: isVertical ? '1px' : '1px',
                  zIndex: 25,
                  ...posStyle
                }}
              >
                {patches.map((patch) => (
                  <div key={patch.id} className="flex flex-col items-center">
                    <div 
                      style={{ 
                        width: `${patchW}px`, 
                        height: `${patchH}px`, 
                        backgroundColor: patch.color 
                      }} 
                      className="border border-black/30 shadow-2xs"
                      title={`${patch.name}: ${patch.color}`}
                    />
                    {showNames && (
                      <span className="text-[7px] font-mono text-zinc-900 leading-tight select-none mt-0.5 max-w-[28px] truncate">
                        {patch.name}
                      </span>
                    )}
                  </div>
                ))}
              </div>
            );
          })()}

          {/* Trim Box and Media Box Visual Overlays (Prepress Standard) */}
          {layout.showPdfBoxes !== false && (() => {
            const boxes = calculatePdfBoxes(layout, template);
            const trimBoxW = boxes.trimBox.width * previewScale;
            const trimBoxH = boxes.trimBox.height * previewScale;
            const trimLeft = (sheetWidth - trimBoxW) / 2;
            const trimTop = (sheetHeight - trimBoxH) / 2;

            const mTop = (Number(layout.mediaBoxMarginTop !== undefined ? layout.mediaBoxMarginTop : 14.17)) * previewScale;
            const mBottom = (Number(layout.mediaBoxMarginBottom !== undefined ? layout.mediaBoxMarginBottom : 14.17)) * previewScale;
            const mLeft = (Number(layout.mediaBoxMarginLeft !== undefined ? layout.mediaBoxMarginLeft : 14.17)) * previewScale;
            const mRight = (Number(layout.mediaBoxMarginRight !== undefined ? layout.mediaBoxMarginRight : 14.17)) * previewScale;

            const mediaLeft = trimLeft - mLeft;
            const mediaTop = trimTop - mTop;
            const mediaW = trimBoxW + mLeft + mRight;
            const mediaH = trimBoxH + mTop + mBottom;

            return (
              <>
                {/* 1. Media Box (Blue Dotted Outline + Label matching reference) */}
                <div 
                  className="absolute pointer-events-none border border-blue-500 border-dashed transition-all"
                  style={{
                    left: `${mediaLeft}px`,
                    top: `${mediaTop}px`,
                    width: `${mediaW}px`,
                    height: `${mediaH}px`,
                    zIndex: 35
                  }}
                >
                  {/* Media Box label with down arrow indicator */}
                  <div className="absolute -top-7 left-4 flex items-center gap-1.5 bg-blue-950/95 text-blue-200 text-[10px] font-mono px-2 py-0.5 rounded shadow border border-blue-400/60 select-none">
                    <span className="text-red-400 font-bold text-xs">↓</span>
                    <span className="font-semibold text-white">Media Box</span>
                    <span className="text-blue-300 text-[9px] opacity-85">({Math.round(boxes.mediaBox.width)} × {Math.round(boxes.mediaBox.height)} pt)</span>
                  </div>
                </div>

                {/* 2. Trim Box (Solid Blue/Red Outline + Label matching reference) */}
                <div 
                  className="absolute pointer-events-none border-2 border-blue-600 transition-all shadow-xs"
                  style={{
                    left: `${trimLeft}px`,
                    top: `${trimTop}px`,
                    width: `${trimBoxW}px`,
                    height: `${trimBoxH}px`,
                    zIndex: 36
                  }}
                >
                  {/* Trim Box label with down arrow indicator */}
                  <div className="absolute -top-7 right-4 flex items-center gap-1.5 bg-blue-600 text-white text-[10px] font-mono px-2 py-0.5 rounded shadow border border-blue-300/80 select-none">
                    <span className="text-amber-300 font-bold text-xs">↓</span>
                    <span className="font-semibold">Trim Box</span>
                    <span className="text-blue-100 text-[9px] opacity-90">({Math.round(boxes.trimBox.width)} × {Math.round(boxes.trimBox.height)} pt)</span>
                  </div>
                </div>
              </>
            );
          })()}
          </div>
        </div>

        {/* Draggable Sheet Layout & Bleed Guide Window */}
        <DraggableFloatingWindow
          title="Sheet Layout & Bleed Guide"
          icon={Info}
          badge="Legend"
          badgeColor="blue"
          initialX={30}
          initialY={70}
          widthClass="w-80 sm:w-88"
          isOpen={showLayoutOverlay}
          onClose={() => setShowLayoutOverlay(false)}
        >
          <div className="space-y-3 p-1 text-xs text-zinc-200">
            {/* Specs Summary Grid */}
            <div className="grid grid-cols-2 gap-2 text-[11px] font-mono">
              <div className="bg-zinc-900/90 p-2 rounded border border-zinc-800">
                <div className="text-[9px] text-zinc-400 font-sans uppercase font-semibold">Sheet Size</div>
                <div className="font-bold text-blue-300 truncate">{layout.pageSize || 'A4'} ({formatUnit(finalSheetWidth, appUnit)} × {formatUnit(finalSheetHeight, appUnit)} {appUnit})</div>
              </div>
              <div className="bg-zinc-900/90 p-2 rounded border border-zinc-800">
                <div className="text-[9px] text-zinc-400 font-sans uppercase font-semibold">Grid Layout</div>
                <div className="font-bold text-emerald-300 truncate">{cols} Cols × {rows} Rows ({cols * rows} Up)</div>
              </div>
              <div className="bg-zinc-900/90 p-2 rounded border border-zinc-800">
                <div className="text-[9px] text-zinc-400 font-sans uppercase font-semibold">Sticker Trim</div>
                <div className="font-bold text-zinc-200 truncate">{formatUnit(template.width, appUnit)} × {formatUnit(template.height, appUnit)} {appUnit}</div>
              </div>
              <div className="bg-zinc-900/90 p-2 rounded border border-zinc-800">
                <div className="text-[9px] text-zinc-400 font-sans uppercase font-semibold">Bleed & Safe Zone</div>
                <div className="font-bold text-amber-300 truncate">Bleed: {formatUnit(layout.bleed || 0, appUnit)} {appUnit}</div>
              </div>
            </div>

            {/* Margins & Gaps */}
            <div className="flex justify-between items-center text-[10px] text-zinc-400 bg-zinc-900/60 px-2 py-1 rounded border border-zinc-800/80 font-mono">
              <span>Margins: X:{formatUnit(layout.marginX || 0, appUnit)} Y:{formatUnit(layout.marginY || 0, appUnit)} {appUnit}</span>
              <span>Gaps: X:{formatUnit(layout.gapX || 0, appUnit)} Y:{formatUnit(layout.gapY || 0, appUnit)} {appUnit}</span>
            </div>

            {/* Sheet Utilization Gauge */}
            <div>
              <div className="flex justify-between text-[10px] mb-1">
                <span className="text-zinc-400 font-medium">Paper Area Efficiency</span>
                <span className="font-bold font-mono text-emerald-400">{efficiencyPercent}%</span>
              </div>
              <div className="w-full bg-zinc-800 h-1.5 rounded-full overflow-hidden">
                <div 
                  className="bg-gradient-to-r from-blue-500 to-emerald-400 h-full transition-all duration-300"
                  style={{ width: `${efficiencyPercent}%` }}
                />
              </div>
            </div>

            {/* Visual Map Legend */}
            <div className="pt-2 border-t border-zinc-800/80 space-y-1 text-[10px]">
              <div className="text-[9px] font-semibold text-zinc-400 uppercase tracking-wider mb-1">Visual Legend</div>
              <div className="grid grid-cols-2 gap-1.5 text-zinc-300">
                <div className="flex items-center gap-1.5">
                  <span className="w-3 h-0 border-t-2 border-dashed border-red-400 inline-block" />
                  <span>Bleed ({formatUnit(layout.bleed || 0, appUnit)}{appUnit})</span>
                </div>
                <div className="flex items-center gap-1.5">
                  <span className="w-3 h-2 bg-blue-100/60 border border-orange-400 inline-block rounded-2xs" />
                  <span>Sticker Trim</span>
                </div>
                <div className="flex items-center gap-1.5">
                  <span className="w-3 h-0 border-t-2 border-dashed border-emerald-400 inline-block" />
                  <span>Safe Zone ({formatUnit(layout.safeZone !== undefined ? layout.safeZone : 8.5, appUnit)}{appUnit})</span>
                </div>
                <div className="flex items-center gap-1.5">
                  <span className="w-3 h-0 border-t border-dotted border-red-300 inline-block" />
                  <span>Page Margins</span>
                </div>
              </div>
            </div>

            <div className="pt-2 border-t border-zinc-800 flex justify-end">
              <button
                type="button"
                onClick={() => setShowLayoutOverlay(false)}
                className="px-3 py-1 bg-zinc-800 hover:bg-zinc-700 text-white rounded text-xs font-bold transition-colors cursor-pointer"
              >
                Close Guide
              </button>
            </div>
          </div>
        </DraggableFloatingWindow>
      </div>

      {/* Draggable Safe Zone Config Window */}
      <DraggableFloatingWindow
        title="Safe Zone Inspector"
        icon={ShieldCheck}
        badge="Visual Guide"
        badgeColor="emerald"
        initialX={Math.max(20, window.innerWidth - 440)}
        initialY={70}
        isOpen={showSafeZoneModal}
        onClose={() => setShowSafeZoneModal(false)}
      >
        <SafeZonePanel onClose={() => setShowSafeZoneModal(false)} />
      </DraggableFloatingWindow>

      {/* Draggable Color Bars Config Window */}
      <DraggableFloatingWindow
        title="Color Bars & CMYK Presets"
        icon={Palette}
        badge="Prepress"
        badgeColor="purple"
        initialX={Math.max(20, window.innerWidth - 460)}
        initialY={70}
        isOpen={showColorBarsModal}
        onClose={() => setShowColorBarsModal(false)}
      >
        <ColorBarsConfigPanel onClose={() => setShowColorBarsModal(false)} />
      </DraggableFloatingWindow>

      {/* Draggable Trim Box & Media Box Config Window */}
      <DraggableFloatingWindow
        title="Trim & Media Box Inspector"
        icon={Scissors}
        badge="PDF Boxes"
        badgeColor="blue"
        initialX={Math.max(20, window.innerWidth - 480)}
        initialY={70}
        isOpen={showTrimMediaModal}
        onClose={() => setShowTrimMediaModal(false)}
      >
        <TrimBoxMediaBoxPanel onClose={() => setShowTrimMediaModal(false)} />
      </DraggableFloatingWindow>

      {/* Draggable Plotter & Finishing Config Window */}
      <DraggableFloatingWindow
        title="Cutting Plotter & CutContour"
        icon={Target}
        badge="Phase 1"
        badgeColor="pink"
        initialX={Math.max(20, window.innerWidth - 500)}
        initialY={70}
        isOpen={showPlotterModal}
        onClose={() => setShowPlotterModal(false)}
      >
        <PlotterFinishingPanel onClose={() => setShowPlotterModal(false)} />
      </DraggableFloatingWindow>

      {/* Draggable Multi-Job Ganging & Work-and-Turn Window (Phase 3) */}
      <DraggableFloatingWindow
        title="Ganging & Work-and-Turn Studio"
        icon={Frame}
        badge="Phase 3"
        badgeColor="purple"
        initialX={Math.max(20, window.innerWidth - 520)}
        initialY={70}
        isOpen={showGangingModal}
        onClose={() => setShowGangingModal(false)}
      >
        <GangingAndNestingPanel onClose={() => setShowGangingModal(false)} />
      </DraggableFloatingWindow>

      {/* Interactive 3D Client Proof Viewer Modal (Phase 4) */}
      {show3DProofModal && (
        
          <ProofViewer3DModal
            isOpen={show3DProofModal}
            onClose={() => setShow3DProofModal(false)}
          />
        
      )}
    </div>
  );
};
